import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  Tooltip,
  Divider,
  MenuItem,
  LinearProgress,
  TablePagination,
  Chip,
  Alert,
  Checkbox,
  Tabs,
  Tab,
  Avatar,
  Stack,
  Switch,
  FormControlLabel,
  Autocomplete,
  CircularProgress,
  Radio,
  RadioGroup
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  School as SchoolIcon, 
  Person as PersonIcon, 
  People as GroupIcon,
  FileUpload as ImportIcon,
  Help as HelpIcon,
  FileDownload as DownloadIcon,
  CallMerge as MergeIcon,
  Warning as WarningIcon,
  LockReset as ResetIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  SupervisorAccount as SupervisorIcon,
  Close as CloseIcon,
  ManageAccounts as ManageIcon,
  Category as CategoryIcon,
  PersonAdd as PersonAddIcon,
  SwapHoriz as TransferIcon,
  AutoFixHigh as AutoFixHighIcon,
  ContentCut as ContentCutIcon,
  FindReplace as FindReplaceIcon,
  Tune as TuneIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { SCHOOL_SIZES } from './StudentDemandSurvey';
import { DEFAULT_STUDENT_MAJORS } from '../constants/majors';

const MAJORS = DEFAULT_STUDENT_MAJORS;

const PREFIX_OPTIONS = [
  'นาย',
  'นาง',
  'นางสาว',
  'ดร.',
  'อ.',
  'ผศ.',
  'ผศ.ดร.',
  'รศ.',
  'รศ.ดร.',
  'ศ.',
  'ศ.ดร.',
  'ว่าที่ ร.ต.',
  'ว่าที่ ร.ต. หญิง',
  'ว่าที่ร้อยตรี',
  'ครู'
];

const STANDARD_AFFILIATIONS = [
  'สำนักงานคณะกรรมการการศึกษาขั้นพื้นฐาน (สพฐ.)',
  'องค์กรปกครองส่วนท้องถิ่น (กถ.)',
  'สำนักงานคณะกรรมการส่งเสริมการศึกษาเอกชน (สช.)',
  'กระทรวงการอุดมศึกษา วิทยาศาสตร์ วิจัย และนวัตกรรม (อว.)',
  'อื่นๆ (โปรดระบุ)'
];

const SchoolManager = () => {
  const navigate = useNavigate();
  const [schools, setSchools] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [affiliationType, setAffiliationType] = useState('');
  const [customAffiliationText, setCustomAffiliationText] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [importMessage, setImportMessage] = useState({ type: 'info', text: '' });
  const [selected, setSelected] = useState<string[]>([]);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedSchoolForDetails, setSelectedSchoolForDetails] = useState<any>(null);
  
  // Deduplication state
  const [dedupOpen, setDedupOpen] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState<any[]>([]);
  const [dismissedGroups, setDismissedGroups] = useState<any[]>([]);
  const [dedupTab, setDedupTab] = useState(0);
  const [dedupLoading, setDedupLoading] = useState(false);

  const handleOpenDedupModal = async () => {
    setDedupOpen(true);
    setDedupLoading(true);
    try {
      const [dupRes, disRes] = await Promise.all([
        api.get('/schools/duplicates'),
        api.get('/schools/duplicates/dismissed').catch(() => ({ data: [] }))
      ]);
      setDuplicateGroups(dupRes.data);
      setDismissedGroups(disRes.data);
    } catch (err) {
      console.error('Failed to fetch duplicates', err);
      alert('เกิดข้อผิดพลาดในการตรวจสอบโรงเรียนซ้ำซ้อน');
    } finally {
      setDedupLoading(false);
    }
  };

  const handleDismissGroup = async (group: any) => {
    const confirmMsg = `ยืนยันว่ากลุ่มสถานศึกษานี้ "ไม่ซ้ำ" กัน?\n\n` +
      `• กลุ่มชื่อ: "${group.name}"\n` +
      `• จำนวนโรงเรียนในกลุ่ม: ${group.schools.length} แห่ง\n\n` +
      `* ระบบจะระบุว่ากลุ่มนี้ไม่ซ้ำ และยกเว้นจากการแจ้งเตือนข้อมูลซ้ำซ้อน (สามารถยกเลิกการยกเว้นได้ในแท็บ 'รายการที่ระบุว่าไม่ซ้ำ')`;

    if (!window.confirm(confirmMsg)) return;

    setDedupLoading(true);
    try {
      await api.post('/schools/duplicates/dismiss', {
        normalizedName: group.normalized_name,
        groupName: group.name,
        schoolIds: group.schools.map((s: any) => s.id)
      });
      alert('บันทึกสถานะ "ไม่ซ้ำ" เรียบร้อยแล้ว ระบบได้ยกเว้นกลุ่มนี้จากการตรวจสอบ');
      const [dupRes, disRes] = await Promise.all([
        api.get('/schools/duplicates'),
        api.get('/schools/duplicates/dismissed').catch(() => ({ data: [] }))
      ]);
      setDuplicateGroups(dupRes.data);
      setDismissedGroups(disRes.data);
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการบันทึกสถานะไม่ซ้ำ');
    } finally {
      setDedupLoading(false);
    }
  };

  const handleRestoreDismissedGroup = async (item: any) => {
    if (!window.confirm(`ยืนยันการยกเลิกการยกเว้นกลุ่ม "${item.name}"?\n(กลุ่มนี้จะกลับมาแสดงในการตรวจจับซ้ำหากมีเงื่อนไขเข้าเกณฑ์)`)) {
      return;
    }

    setDedupLoading(true);
    try {
      await api.post('/schools/duplicates/restore', {
        key: item.key,
        idsKey: item.ids_key
      });
      alert('ยกเลิกการยกเว้นสำเร็จ รายการจะกลับมาตรวจจับตามปกติ');
      const [dupRes, disRes] = await Promise.all([
        api.get('/schools/duplicates'),
        api.get('/schools/duplicates/dismissed').catch(() => ({ data: [] }))
      ]);
      setDuplicateGroups(dupRes.data);
      setDismissedGroups(disRes.data);
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการยกเลิกการยกเว้น');
    } finally {
      setDedupLoading(false);
    }
  };

  const handleMergeGroup = async (group: any, canonicalId: string) => {
    const dupIds = group.schools.map((s: any) => s.id).filter((id: string) => id !== canonicalId);
    if (dupIds.length === 0) return;

    const canonicalSchool = group.schools.find((s: any) => s.id === canonicalId);
    const totalStudents = group.schools.reduce((sum: number, s: any) => sum + (Number(s.placement_count) || 0), 0);
    const totalCoTeachers = group.schools.reduce((sum: number, s: any) => sum + (Number(s.coteacher_count) || 0), 0);

    const confirmMsg = `ยืนยันการรวมข้อมูลสถานศึกษาที่ซ้ำซ้อนเข้าด้วยกัน?\n\n` +
      `• โรงเรียนหลักที่จะใช้: "${canonicalSchool?.name}" (ID: ${canonicalId.slice(0, 8)}...)\n` +
      `• จำนวนโรงเรียนที่จะถูกยุบรวม: ${dupIds.length} แห่ง\n` +
      `• นักศึกษาที่จะย้ายมารวมกัน: ${totalStudents} คน\n` +
      `• ครูพี่เลี้ยงที่จะย้ายมารวมกัน: ${totalCoTeachers} คน\n\n` +
      `* ข้อมูลนักศึกษา ผลการประเมิน คะแนนภาระงาน และครูพี่เลี้ยงทั้งหมดจะถูกย้ายมารวมอยู่ภายใต้โรงเรียนหลักนี้อย่างสมบูรณ์ และรายการซ้ำซ้อนจะถูกปิดการใช้งานอย่างปลอดภัย`;

    if (!window.confirm(confirmMsg)) {
      return;
    }

    setDedupLoading(true);
    try {
      const res = await api.post('/schools/merge', {
        targetSchoolId: canonicalId,
        duplicateSchoolIds: dupIds
      });
      alert(res.data?.message || 'รวมข้อมูลโรงเรียนสำเร็จเรียบร้อย');
      fetchSchools();
      // Refresh duplicates list
      const [dupRes, disRes] = await Promise.all([
        api.get('/schools/duplicates'),
        api.get('/schools/duplicates/dismissed').catch(() => ({ data: [] }))
      ]);
      setDuplicateGroups(dupRes.data);
      setDismissedGroups(disRes.data);
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการรวมข้อมูล');
    } finally {
      setDedupLoading(false);
    }
  };

  // Batch School Text & Affiliation Tool Modal State
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [batchScope, setBatchScope] = useState<'ALL' | 'FILTERED' | 'SELECTED'>('SELECTED');
  const [batchTab, setBatchTab] = useState<number>(0); // 0: Trim, 1: Find & Replace, 2: Batch Affiliation
  
  // Tab 0: Trim Prefix/Suffix
  const [trimText, setTrimText] = useState('โรงเรียน');
  const [trimType, setTrimType] = useState<'PREFIX' | 'SUFFIX' | 'TRIM_SPACES'>('PREFIX');
  
  // Tab 1: Find & Replace
  const [targetField, setTargetField] = useState<'name' | 'affiliation' | 'address'>('name');
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [caseSensitive, setCaseSensitive] = useState(false);

  // Tab 2: Batch Affiliation
  const [batchAffiliationChoice, setBatchAffiliationChoice] = useState('');
  const [customBatchAffiliation, setCustomBatchAffiliation] = useState('');

  // Row selection exclusions in the preview table
  const [excludedBatchIds, setExcludedBatchIds] = useState<string[]>([]);
  const [batchSaving, setBatchSaving] = useState(false);

  const handleOpenBatchTextTool = () => {
    if (selected.length > 0) {
      setBatchScope('SELECTED');
    } else if (searchTerm.trim()) {
      setBatchScope('FILTERED');
    } else {
      setBatchScope('ALL');
    }
    setExcludedBatchIds([]);
    setBatchModalOpen(true);
  };

  const [detailsTab, setDetailsTab] = useState(0);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Term / Academic year filters
  const [selectedSemester, setSelectedSemester] = useState<string>('all');
  const [selectedYear, setSelectedYear] = useState<string>('all');
  const [availableYears, setAvailableYears] = useState<string[]>([]);

  // Co-teacher management (Edit / Relocate)
  const [coTeacherManageOpen, setCoTeacherManageOpen] = useState(false);
  const [selectedCoTeacher, setSelectedCoTeacher] = useState<any>(null);
  const [coTeacherFormData, setCoTeacherFormData] = useState<any>({
    prefix: 'นาย',
    first_name: '',
    last_name: '',
    school_id: '',
    position: '',
    username: '',
    email: '',
    phone_number: '',
    is_active: true
  });
  const [coTeacherSaving, setCoTeacherSaving] = useState(false);

  // Co-teacher creation modal
  const [createCoTeacherOpen, setCreateCoTeacherOpen] = useState(false);
  const [createCoTeacherSaving, setCreateCoTeacherSaving] = useState(false);
  const [createCoTeacherFormData, setCreateCoTeacherFormData] = useState<any>({
    prefix: 'นาย',
    first_name: '',
    last_name: '',
    school_id: '',
    position: '',
    username: '',
    password: '',
    email: '',
    phone_number: ''
  });

  // Co-teacher CSV Import
  const [coTeacherImportOpen, setCoTeacherImportOpen] = useState(false);
  const [coTeacherImportMessage, setCoTeacherImportMessage] = useState({ type: 'info', text: '' });

  // Student relocation modal
  const [relocateOpen, setRelocateOpen] = useState(false);
  const [selectedStudentForRelocate, setSelectedStudentForRelocate] = useState<any>(null);
  const [relocateSchoolId, setRelocateSchoolId] = useState<string>('');
  const [relocateCoTeacherId, setRelocateCoTeacherId] = useState<string>('');
  const [relocateSemester, setRelocateSemester] = useState<string>('1');
  const [relocateYear, setRelocateYear] = useState<string>('2569');
  const [relocateSchoolCoTeachers, setRelocateSchoolCoTeachers] = useState<any[]>([]);
  const [relocateLoadingCoTeachers, setRelocateLoadingCoTeachers] = useState<boolean>(false);
  const [relocateSaving, setRelocateSaving] = useState<boolean>(false);

  const handleShowDetails = async (school: any) => {
    setSelectedSchoolForDetails(school);
    setDetailsOpen(true);
    setDetailsLoading(true);
    setDetailsTab(0);
    try {
      const params: any = {};
      if (selectedSemester && selectedSemester !== 'all') params.semester = selectedSemester;
      if (selectedYear && selectedYear !== 'all') params.academic_year = selectedYear;
      const res = await api.get(`/schools/${school.id}`, { params });
      setSelectedSchoolForDetails(res.data);
    } catch (err) {
      console.error('Failed to fetch detailed school info', err);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedSchoolForDetails(null);
  };

  const handleOpenManageCoTeacher = (ct: any, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setSelectedCoTeacher(ct);
    setCoTeacherFormData({
      prefix: ct.prefix || 'นาย',
      first_name: ct.first_name || '',
      last_name: ct.last_name || '',
      school_id: ct.school_id || selectedSchoolForDetails?.id || '',
      position: ct.position || '',
      username: ct.username || '',
      email: ct.email || '',
      phone_number: ct.phone_number || '',
      is_active: ct.is_active === 1 || ct.is_active === true
    });
    setCoTeacherManageOpen(true);
  };

  const handleSaveCoTeacher = async () => {
    if (!selectedCoTeacher) return;
    if (!coTeacherFormData.first_name?.trim() || !coTeacherFormData.last_name?.trim()) {
      alert('กรุณาระบุชื่อและนามสกุลของครูพี่เลี้ยง');
      return;
    }
    if (!coTeacherFormData.username?.trim()) {
      alert('กรุณากรอกชื่อบัญชีผู้ใช้ (Username)');
      return;
    }
    setCoTeacherSaving(true);
    try {
      await api.put(`/co-teachers/${selectedCoTeacher.id}`, {
        ...coTeacherFormData,
        username: coTeacherFormData.username.trim(),
        prefix: coTeacherFormData.prefix?.trim() || 'นาย',
        school_id: coTeacherFormData.school_id || selectedSchoolForDetails?.id
      });
      alert('บันทึกข้อมูลครูพี่เลี้ยงเรียบร้อยแล้ว');
      setCoTeacherManageOpen(false);
      // Reload fresh school details
      if (selectedSchoolForDetails?.id) {
        const params: any = {};
        if (selectedSemester && selectedSemester !== 'all') params.semester = selectedSemester;
        if (selectedYear && selectedYear !== 'all') params.academic_year = selectedYear;
        const res = await api.get(`/schools/${selectedSchoolForDetails.id}`, { params });
        setSelectedSchoolForDetails(res.data);
      }
      fetchSchools();
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || err.response?.data?.error || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล');
    } finally {
      setCoTeacherSaving(false);
    }
  };

  const handleOpenCreateCoTeacher = (defaultSchoolId?: string) => {
    setCreateCoTeacherFormData({
      prefix: 'นาย',
      first_name: '',
      last_name: '',
      school_id: defaultSchoolId || (schools.length > 0 ? (schools[0] as any).id : ''),
      position: '',
      username: '',
      password: '',
      email: '',
      phone_number: ''
    });
    setCreateCoTeacherOpen(true);
  };

  const handleSaveNewCoTeacher = async () => {
    if (!createCoTeacherFormData.first_name?.trim() || !createCoTeacherFormData.last_name?.trim()) {
      alert('กรุณากรอกชื่อและนามสกุลของครูพี่เลี้ยง');
      return;
    }
    if (!createCoTeacherFormData.school_id) {
      alert('กรุณาเลือกสถานศึกษาที่สังกัด');
      return;
    }
    if (!createCoTeacherFormData.username?.trim()) {
      alert('กรุณากรอกชื่อผู้ใช้งาน (Username)');
      return;
    }
    setCreateCoTeacherSaving(true);
    try {
      await api.post('/co-teachers', {
        ...createCoTeacherFormData,
        username: createCoTeacherFormData.username.trim(),
        prefix: createCoTeacherFormData.prefix?.trim() || 'นาย'
      });
      alert('เพิ่มข้อมูลครูพี่เลี้ยงเรียบร้อยแล้ว');
      setCreateCoTeacherOpen(false);
      if (selectedSchoolForDetails?.id) {
        const params: any = {};
        if (selectedSemester && selectedSemester !== 'all') params.semester = selectedSemester;
        if (selectedYear && selectedYear !== 'all') params.academic_year = selectedYear;
        const res = await api.get(`/schools/${selectedSchoolForDetails.id}`, { params });
        setSelectedSchoolForDetails(res.data);
      }
      fetchSchools();
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || err.response?.data?.error || 'เกิดข้อผิดพลาดในการสร้างครูพี่เลี้ยง');
    } finally {
      setCreateCoTeacherSaving(false);
    }
  };

  const handleOpenRelocateStudent = async (st: any) => {
    setSelectedStudentForRelocate(st);
    const initialSchoolId = selectedSchoolForDetails?.id || '';
    setRelocateSchoolId(initialSchoolId);
    setRelocateCoTeacherId(st.co_teacher_id || '');
    setRelocateSemester(st.semester ? String(st.semester) : (selectedSemester !== 'all' ? selectedSemester : '1'));
    setRelocateYear(st.academic_year ? String(st.academic_year) : (selectedYear !== 'all' ? selectedYear : '2569'));
    setRelocateOpen(true);

    if (initialSchoolId) {
      setRelocateLoadingCoTeachers(true);
      try {
        const res = await api.get(`/schools/${initialSchoolId}`);
        setRelocateSchoolCoTeachers(res.data.co_teachers || []);
      } catch (err) {
        console.error(err);
      } finally {
        setRelocateLoadingCoTeachers(false);
      }
    }
  };

  const handleRelocateSchoolChange = async (newSchoolId: string) => {
    setRelocateSchoolId(newSchoolId);
    setRelocateCoTeacherId('');
    if (!newSchoolId) {
      setRelocateSchoolCoTeachers([]);
      return;
    }
    setRelocateLoadingCoTeachers(true);
    try {
      const res = await api.get(`/schools/${newSchoolId}`);
      setRelocateSchoolCoTeachers(res.data.co_teachers || []);
    } catch (err) {
      console.error('Failed to load co-teachers for target school', err);
    } finally {
      setRelocateLoadingCoTeachers(false);
    }
  };

  const handleSaveRelocateStudent = async () => {
    if (!selectedStudentForRelocate?.placement_id) return;
    if (!relocateSchoolId) {
      alert('กรุณาเลือกสถานศึกษาเป้าหมาย');
      return;
    }
    setRelocateSaving(true);
    try {
      await api.put(`/placements/${selectedStudentForRelocate.placement_id}`, {
        school_id: relocateSchoolId,
        co_teacher_id: relocateCoTeacherId || null,
        semester: relocateSemester,
        academic_year: relocateYear
      });
      alert('โยกย้ายสถานศึกษา / เปลี่ยนครูพี่เลี้ยง สำเร็จเรียบร้อย');
      setRelocateOpen(false);
      if (selectedSchoolForDetails?.id) {
        const params: any = {};
        if (selectedSemester && selectedSemester !== 'all') params.semester = selectedSemester;
        if (selectedYear && selectedYear !== 'all') params.academic_year = selectedYear;
        const res = await api.get(`/schools/${selectedSchoolForDetails.id}`, { params });
        setSelectedSchoolForDetails(res.data);
      }
      fetchSchools();
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || err.response?.data?.error || 'เกิดข้อผิดพลาดในการโยกย้ายข้อมูลนักศึกษา');
    } finally {
      setRelocateSaving(false);
    }
  };

  const handleExportCoTeachersExcel = async () => {
    setLoading(true);
    try {
      const res = await api.get('/co-teachers');
      const exportData = res.data.map((ct: any) => ({
        'คำนำหน้า': ct.prefix || '',
        'ชื่อ': ct.first_name,
        'นามสกุล': ct.last_name,
        'โรงเรียนที่สังกัด': ct.school_name || '-',
        'กลุ่มสาระการเรียนรู้ / ตำแหน่ง': ct.position || '-',
        'ชื่อผู้ใช้งาน (Username)': ct.username,
        'เบอร์โทรศัพท์': ct.phone_number || '-',
        'อีเมล': ct.email || '-',
        'สถานะการใช้งาน': (ct.is_active === 1 || ct.is_active === true) ? 'เปิดใช้งาน' : 'ระงับการใช้งาน'
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'CoTeachers');
      XLSX.writeFile(workbook, `tpms_coteachers_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      console.error(err);
      alert('เกิดข้อผิดพลาดในการส่งออกข้อมูลครูพี่เลี้ยง');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCoTeacherTemplate = () => {
    const headers = ['Username', 'Password', 'คำนำหน้า', 'ชื่อ', 'นามสกุล', 'Email', 'เบอร์โทร', 'ชื่อโรงเรียน', 'กลุ่มสาระ'];
    const sampleRow = ['coteacher01', 'pass1234', 'ครู', 'วิไล', 'ใจดี', 'wilai@gmail.com', '0811112222', 'โรงเรียนสาธิตฯ', 'วิทยาศาสตร์ทั่วไป'];

    const csvContent = Papa.unparse({
      fields: headers,
      data: [sampleRow]
    });

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'coteacher_import_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCoTeacherFileUpload = (e: any) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const parsedCoTeachers = results.data.map((row: any) => {
          const getVal = (synonyms: string[]) => {
            for (const key of Object.keys(row)) {
              if (synonyms.some(s => key.trim().toLowerCase() === s.toLowerCase())) {
                return row[key];
              }
            }
            return undefined;
          };

          const username = getVal(['Username', 'username', 'ชื่อผู้ใช้', 'ชื่อผู้ใช้งาน']) || getVal(['Email', 'email', 'อีเมล', 'อีเมล์', 'E-mail', 'e-mail']);
          const password = getVal(['Password', 'password', 'รหัสผ่าน']) || username;
          const prefix = getVal(['คำนำหน้า', 'คำนำหน้าชื่อ', 'prefix', 'Prefix']) || '';
          const first_name = getVal(['ชื่อ', 'ชื่อจริง', 'first_name', 'first name', 'FirstName']) || '';
          const last_name = getVal(['นามสกุล', 'last_name', 'last name', 'LastName']) || '';
          const email = getVal(['Email', 'email', 'อีเมล', 'อีเมล์', 'E-mail', 'e-mail']) || '';
          const phone_number = getVal(['เบอร์โทร', 'เบอร์โทรศัพท์', 'โทร', 'phone_number', 'phone', 'Tel', 'tel']) || '';
          const school_name = getVal(['ชื่อโรงเรียน', 'โรงเรียน', 'school_name', 'school', 'School']) || '';
          const position = getVal(['กลุ่มสาระ', 'ตำแหน่ง', 'กลุ่มสาระการเรียนรู้', 'position', 'Position']) || '';

          return { username, password, prefix, first_name, last_name, email, phone_number, school_name, position };
        });

        try {
          const res = await api.post('/co-teachers/bulk', { coTeachers: parsedCoTeachers });
          const allResults = res.data.results || [];
          const successCount = allResults.filter((r: any) => r.status === 'imported').length;
          const failedCount = allResults.filter((r: any) => r.status === 'failed').length;

          if (failedCount > 0) {
            const failedDetails = allResults
              .filter((r: any) => r.status === 'failed')
              .map((r: any) => `${r.name || 'ไม่ระบุชื่อ'} (${r.reason})`)
              .join(', ');

            if (successCount === 0) {
              setCoTeacherImportMessage({
                type: 'error',
                text: `การนำเข้าล้มเหลวทั้งหมด: ${failedDetails}`
              });
            } else {
              setCoTeacherImportMessage({
                type: 'warning',
                text: `นำเข้าข้อมูลสำเร็จ ${successCount} ท่าน และล้มเหลว ${failedCount} ท่านเนื่องจาก: ${failedDetails}`
              });
            }
          } else {
            setCoTeacherImportMessage({
              type: 'success',
              text: `นำเข้าข้อมูลสำเร็จทั้งหมด ${successCount} ท่าน`
            });
          }
          fetchSchools();
        } catch (err: any) {
          setCoTeacherImportMessage({ type: 'error', text: err.response?.data?.message || err.response?.data?.error || 'การนำเข้าข้อมูลล้มเหลว' });
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleResetCoTeacherPassword = async () => {
    if (!selectedCoTeacher?.user_id) return;
    const name = `${selectedCoTeacher.prefix || ''}${selectedCoTeacher.first_name} ${selectedCoTeacher.last_name}`;
    const newPassword = window.prompt(`ระบุรหัสผ่านใหม่สำหรับครูพี่เลี้ยง: ${name}`);
    if (newPassword && newPassword.trim()) {
      setCoTeacherSaving(true);
      try {
        await api.post('/auth/admin-reset-password', {
          userId: selectedCoTeacher.user_id,
          newPassword: newPassword.trim()
        });
        alert('รีเซ็ตรหัสผ่านเรียบร้อยแล้ว');
      } catch (err: any) {
        console.error(err);
        alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการรีเซ็ตรหัสผ่าน');
      } finally {
        setCoTeacherSaving(false);
      }
    }
  };

  const handleDeleteCoTeacher = async () => {
    if (!selectedCoTeacher) return;
    const ctName = `${selectedCoTeacher.prefix || ''}${selectedCoTeacher.first_name} ${selectedCoTeacher.last_name}`;
    if (!window.confirm(`ยืนยันการลบข้อมูลครูพี่เลี้ยง "${ctName}" ออกจากระบบ? (สามารถกู้คืนได้จากถังขยะ)`)) {
      return;
    }
    setCoTeacherSaving(true);
    try {
      await api.delete(`/co-teachers/${selectedCoTeacher.id}`);
      alert('ลบข้อมูลครูพี่เลี้ยงสำเร็จ (ย้ายลงถังขยะเรียบร้อย)');
      setCoTeacherManageOpen(false);
      if (selectedSchoolForDetails?.id) {
        const res = await api.get(`/schools/${selectedSchoolForDetails.id}`);
        setSelectedSchoolForDetails(res.data);
      }
      fetchSchools();
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการลบข้อมูล');
    } finally {
      setCoTeacherSaving(false);
    }
  };
  
  const initialCapacities = MAJORS.reduce((acc: any, major) => {
    acc[major] = 0;
    return acc;
  }, {});

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    affiliation: '',
    principal_prefix: 'นาย',
    principal_first_name: '',
    principal_last_name: '',
    principal_phone: '',
    capacity: 0,
    major_capacities: initialCapacities
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchSchools = async (sem = selectedSemester, yr = selectedYear) => {
    setLoading(true);
    try {
      const params: any = {};
      if (sem && sem !== 'all') params.semester = sem;
      if (yr && yr !== 'all') params.academic_year = yr;
      const response = await api.get('/schools', { params });
      setSchools(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initFilters = async () => {
      try {
        const settingsRes = await api.get('/settings');
        const curSem = settingsRes.data?.current_semester || '1';
        const curYr = settingsRes.data?.current_academic_year || '2569';
        setSelectedSemester(curSem);
        setSelectedYear(curYr);
        
        const baseYear = parseInt(curYr) || 2569;
        const years = [baseYear - 2, baseYear - 1, baseYear, baseYear + 1, baseYear + 2].map(String);
        setAvailableYears(years);

        fetchSchools(curSem, curYr);
      } catch (err) {
        console.error('Failed to init settings', err);
        fetchSchools();
      }
    };
    initFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterSemesterChange = (newSem: string) => {
    setSelectedSemester(newSem);
    fetchSchools(newSem, selectedYear);
  };

  const handleFilterYearChange = (newYr: string) => {
    setSelectedYear(newYr);
    fetchSchools(selectedSemester, newYr);
  };

  const processSchoolRows = async (rows: any[]) => {
    const parsedSchools = rows.map((row: any) => {
      const majorCaps = MAJORS.reduce((acc: any, m) => {
        acc[m] = parseInt(row[m] ?? row[m.replace(/\s+/g, '')]) || 0;
        return acc;
      }, {});

      return {
        id: row['รหัสอ้างอิง'] || row['รหัสอ้างอิง (กรณีแก้ไขโรงเรียนเดิม เว้นว่างหากเพิ่มใหม่)'] || row['School ID'] || row['school_id'] || row['id'] || undefined,
        name: row['ชื่อโรงเรียน'] || row['name'],
        affiliation: row['สังกัด'] || row['affiliation'],
        address: row['ที่อยู่'] || row['address'],
        principal_prefix: row['คำนำหน้าผู้บริหาร'] || row['principal_prefix'] || 'นาย',
        principal_first_name: row['ชื่อผู้บริหาร'] || row['principal_first_name'],
        principal_last_name: row['นามสกุลผู้บริหาร'] || row['principal_last_name'],
        principal_phone: row['เบอร์โทรผู้บริหาร'] || row['principal_phone'],
        major_capacities: majorCaps
      };
    }).filter((s: any) => s.name || s.id);

    try {
      const res = await api.post('/schools/bulk', { schools: parsedSchools });
      setImportMessage({ type: 'success', text: `นำเข้า/อัปเดตข้อมูลสำเร็จ ${res.data.results?.length || parsedSchools.length} โรงเรียน` });
      fetchSchools();
    } catch (err: any) {
      setImportMessage({ type: 'error', text: err.response?.data?.message || 'การนำเข้าข้อมูลล้มเหลว' });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (e: any) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    setImportMessage({ type: 'info', text: 'กำลังประมวลผลไฟล์...' });

    const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');

    if (isExcel) {
      const reader = new FileReader();
      reader.onload = async (evt: any) => {
        try {
          const data = evt.target.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
          await processSchoolRows(jsonData);
        } catch (err: any) {
          setImportMessage({ type: 'error', text: 'ไม่สามารถอ่านไฟล์ Excel ได้: ' + (err.message || '') });
          setLoading(false);
        }
      };
      reader.readAsBinaryString(file);
    } else {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: async (results) => {
          await processSchoolRows(results.data);
        },
        error: (err) => {
          setImportMessage({ type: 'error', text: 'ไม่สามารถอ่านไฟล์ CSV ได้: ' + err.message });
          setLoading(false);
        }
      });
    }
  };

  // Compute batch preview list
  const getBatchSourceSchools = () => {
    if (batchScope === 'SELECTED' && selected.length > 0) {
      return schools.filter((s: any) => selected.includes(s.id));
    }
    if (batchScope === 'FILTERED') {
      return filteredSchools;
    }
    return schools;
  };

  const getComputedBatchPreview = () => {
    const sourceSchools = getBatchSourceSchools();
    
    return sourceSchools.map((s: any) => {
      let newName = s.name || '';
      let newAffiliation = s.affiliation || '';
      let newAddress = s.address || '';

      if (batchTab === 0) {
        // Trim
        if (trimType === 'TRIM_SPACES') {
          newName = newName.trim();
        } else if (trimText && trimType === 'PREFIX') {
          const tText = trimText.trim();
          if (newName.startsWith(tText)) {
            newName = newName.substring(tText.length).trim();
          } else if (newName.startsWith(trimText)) {
            newName = newName.substring(trimText.length).trim();
          }
        } else if (trimText && trimType === 'SUFFIX') {
          const tText = trimText.trim();
          if (newName.endsWith(tText)) {
            newName = newName.substring(0, newName.length - tText.length).trim();
          } else if (newName.endsWith(trimText)) {
            newName = newName.substring(0, newName.length - trimText.length).trim();
          }
        }
      } else if (batchTab === 1) {
        // Find & Replace
        if (findText) {
          try {
            const escapeReg = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const regex = new RegExp(escapeReg(findText), caseSensitive ? 'g' : 'gi');
            if (targetField === 'name') {
              newName = (s.name || '').replace(regex, replaceText);
            } else if (targetField === 'affiliation') {
              newAffiliation = (s.affiliation || '').replace(regex, replaceText);
            } else if (targetField === 'address') {
              newAddress = (s.address || '').replace(regex, replaceText);
            }
          } catch (e) {
            console.error('Regex error', e);
          }
        }
      } else if (batchTab === 2) {
        // Batch Affiliation
        const targetAff = batchAffiliationChoice === 'อื่นๆ (โปรดระบุ)'
          ? customBatchAffiliation.trim()
          : batchAffiliationChoice;
        if (targetAff) {
          newAffiliation = targetAff;
        }
      }

      const hasChanged = (newName !== (s.name || '')) ||
                         (newAffiliation !== (s.affiliation || '')) ||
                         (newAddress !== (s.address || ''));

      return {
        id: s.id,
        currentName: s.name || '',
        newName,
        currentAffiliation: s.affiliation || '',
        newAffiliation,
        currentAddress: s.address || '',
        newAddress,
        hasChanged
      };
    });
  };

  const handleExecuteBatchUpdate = async () => {
    const previewList = getComputedBatchPreview();
    const changedList = previewList.filter(p => p.hasChanged && !excludedBatchIds.includes(p.id));

    if (changedList.length === 0) {
      alert('ไม่พบรายการที่มีการเปลี่ยนแปลงที่จะบันทึก');
      return;
    }

    if (!window.confirm(`ยืนยันการบันทึกการแก้ไขข้อมูลสถานศึกษาจำนวน ${changedList.length} แห่ง?`)) {
      return;
    }

    setBatchSaving(true);
    try {
      const updates = changedList.map(p => ({
        id: p.id,
        name: p.newName,
        affiliation: p.newAffiliation,
        address: p.newAddress
      }));

      const res = await api.post('/schools/batch-update', { updates });
      alert(res.data.message || `อัปเดตข้อมูลสำเร็จ ${updates.length} โรงเรียน`);
      setBatchModalOpen(false);
      setExcludedBatchIds([]);
      setSelected([]);
      fetchSchools();
    } catch (err: any) {
      console.error(err);
      alert('เกิดข้อผิดพลาดในการบันทึกข้อมูล: ' + (err.response?.data?.message || err.message));
    } finally {
      setBatchSaving(false);
    }
  };

  const filteredSchools = schools.filter((s: any) => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return true;
    return (
      s.name?.toLowerCase().includes(term) ||
      s.affiliation?.toLowerCase().includes(term) ||
      `${s.principal_first_name || ''} ${s.principal_last_name || ''}`.toLowerCase().includes(term) ||
      (s.co_teacher_names && s.co_teacher_names.toLowerCase().includes(term)) ||
      (s.student_names && s.student_names.toLowerCase().includes(term))
    );
  });

  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = filteredSchools.map((n: any) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleSelectOne = (id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const handleBulkDelete = async () => {
    if (window.confirm(`ยืนยันการย้ายสถานศึกษาที่เลือก ${selected.length} รายการ ไปยังถังขยะ?`)) {
      setLoading(true);
      try {
        await api.post('/schools/bulk-soft-delete', { ids: selected });
        setSelected([]);
        fetchSchools();
        alert('ย้ายข้อมูลไปยังถังขยะเรียบร้อยแล้ว');
      } catch (err) {
        console.error(err);
        alert('เกิดข้อผิดพลาดในการลบข้อมูล');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleOpen = (school: any = null) => {
    if (school) {
      setEditingId(school.id);
      const isPredefined = STANDARD_AFFILIATIONS.slice(0, 4).includes(school.affiliation);
      if (isPredefined) {
        setAffiliationType(school.affiliation);
        setCustomAffiliationText('');
      } else if (school.affiliation) {
        setAffiliationType('อื่นๆ (โปรดระบุ)');
        setCustomAffiliationText(school.affiliation);
      } else {
        setAffiliationType('');
        setCustomAffiliationText('');
      }
      const normalizedCaps: Record<string, number> = {};
      MAJORS.forEach(m => {
        normalizedCaps[m] = parseInt(school.major_capacities?.[m] ?? school.major_capacities?.[m.replace(/\s+/g, '')]) || 0;
      });
      setFormData({
        name: school.name,
        address: school.address,
        affiliation: school.affiliation,
        principal_prefix: school.principal_prefix || 'นาย',
        principal_first_name: school.principal_first_name || '',
        principal_last_name: school.principal_last_name || '',
        principal_phone: school.principal_phone || '',
        capacity: school.capacity,
        major_capacities: normalizedCaps
      });
    } else {
      setEditingId(null);
      setAffiliationType('');
      setCustomAffiliationText('');
      setFormData({ 
        name: '', 
        address: '', 
        affiliation: '', 
        principal_prefix: 'นาย',
        principal_first_name: '',
        principal_last_name: '',
        principal_phone: '',
        capacity: 0, 
        major_capacities: initialCapacities 
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingId(null);
  };

  const handleChange = (e: any) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleMajorCapacityChange = (major: string, value: string) => {
    setFormData({
      ...formData,
      major_capacities: {
        ...formData.major_capacities,
        [major]: parseInt(value) || 0
      }
    });
  };

  const handleSubmit = async () => {
    let finalAffiliation = formData.affiliation;
    if (affiliationType === 'อื่นๆ (โปรดระบุ)') {
      if (!customAffiliationText.trim()) {
        alert('กรุณาระบุสังกัดสถานศึกษา');
        return;
      }
      finalAffiliation = customAffiliationText.trim();
    } else if (affiliationType) {
      finalAffiliation = affiliationType;
    }

    if (!finalAffiliation) {
      alert('กรุณาเลือกหรือระบุสังกัดสถานศึกษา');
      return;
    }

    const payload = {
      ...formData,
      affiliation: finalAffiliation
    };

    setLoading(true);
    try {
      if (editingId) {
        await api.put(`/schools/${editingId}`, payload);
      } else {
        await api.post('/schools', payload);
      }
      handleClose();
      fetchSchools();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('ยืนยันการลบข้อมูลโรงเรียน? รายการจะถูกย้ายไปยังถังขยะ')) {
      setLoading(true);
      try {
        await api.delete(`/schools/${id}`);
        fetchSchools();
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleExportExcel = () => {
    const exportData = filteredSchools.map((s: any) => {
      const row: any = {
        'รหัสอ้างอิง': s.id,
        'ชื่อโรงเรียน': s.name,
        'สังกัด': s.affiliation,
        'ที่อยู่': s.address,
        'คำนำหน้าผู้บริหาร': s.principal_prefix,
        'ชื่อผู้บริหาร': s.principal_first_name,
        'นามสกุลผู้บริหาร': s.principal_last_name,
        'เบอร์โทรผู้บริหาร': s.principal_phone,
        'ความจุรวม': s.capacity
      };

      // Add major capacities
      MAJORS.forEach(m => {
        row[m] = s.major_capacities?.[m] ?? s.major_capacities?.[m.replace(/\s+/g, '')] ?? 0;
      });

      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Schools');
    XLSX.writeFile(workbook, `tpms_schools_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleDownloadExcelTemplate = () => {
    const sampleRow: any = {
      'รหัสอ้างอิง': '',
      'ชื่อโรงเรียน': 'สาธิตมหาวิทยาลัยราชภัฏกำแพงเพชร',
      'สังกัด': 'กระทรวงการอุดมศึกษา วิทยาศาสตร์ วิจัย และนวัตกรรม (อว.)',
      'ที่อยู่': '69 หมู่ 1 ต.นครชุม อ.เมือง จ.กำแพงเพชร',
      'คำนำหน้าผู้บริหาร': 'นาย',
      'ชื่อผู้บริหาร': 'สมชาย',
      'นามสกุลผู้บริหาร': 'ใจดี',
      'เบอร์โทรผู้บริหาร': '055706555',
      'ความจุรวม': 15
    };
    MAJORS.forEach(m => {
      sampleRow[m] = 1;
    });

    const worksheet = XLSX.utils.json_to_sheet([sampleRow]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'SchoolTemplate');
    XLSX.writeFile(workbook, 'tpms_school_import_template.xlsx');
  };

  const handleDownloadTemplate = () => {
    const headers = [
      'รหัสอ้างอิง', 'ชื่อโรงเรียน', 'สังกัด', 'ที่อยู่', 'คำนำหน้าผู้บริหาร', 'ชื่อผู้บริหาร', 'นามสกุลผู้บริหาร', 'เบอร์โทรผู้บริหาร',
      ...MAJORS
    ];
    
    const sampleRow = [
      '', 'โรงเรียนสาธิตฯ', 'สพม.กำแพงเพชร', '123 ต.ในเมือง อ.เมือง จ.กำแพงเพชร', 'นาย', 'สมชาย', 'สายเสมอ', '0812345678',
      ...MAJORS.map(() => '0')
    ];

    const csvContent = Papa.unparse({
      fields: headers,
      data: [sampleRow]
    });

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'school_import_template_tpms.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {loading && <LinearProgress sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 2000 }} />}
      {/* Header and Primary Creation CTAs */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', md: 'center' }, 
        gap: 2, 
        mb: 2 
      }}>
        <Box>
          <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' }, fontWeight: 800, color: 'text.primary' }}>
            จัดการสถานศึกษาและครูพี่เลี้ยง (School & Co-Teacher Management)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ระบบบริหารจัดการเครือข่ายสถานศึกษาฝึกประสบการณ์ อัตราการรับ และข้อมูลครูพี่เลี้ยง
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap' }}>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<SchoolIcon />} 
            onClick={() => handleOpen()}
            sx={{ borderRadius: 2, fontWeight: 700, px: 2, boxShadow: 2 }}
          >
            เพิ่มโรงเรียนใหม่
          </Button>
          <Button 
            variant="contained" 
            color="secondary" 
            startIcon={<PersonAddIcon />} 
            onClick={() => handleOpenCreateCoTeacher()}
            sx={{ borderRadius: 2, fontWeight: 700, px: 2, boxShadow: 2 }}
          >
            เพิ่มครูพี่เลี้ยงใหม่
          </Button>
        </Stack>
      </Box>

      {/* Grouped Management Tools Bar */}
      <Paper variant="outlined" sx={{ p: 1.5, px: 2, mb: 3, borderRadius: 2.5, bgcolor: '#fafafa', border: '1px solid rgba(0,0,0,0.06)' }}>
        <Grid container spacing={2} sx={{ alignItems: 'center' }}>
          {/* Group 1: ข้อมูลสถานศึกษา */}
          <Grid size={{ xs: 12, lg: 7 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Chip 
                icon={<SchoolIcon sx={{ fontSize: '1rem !important' }} />} 
                label="หมวดหมู่สถานศึกษา:" 
                size="small" 
                variant="outlined"
                sx={{ fontWeight: 700, bgcolor: 'background.paper', borderColor: 'primary.light' }} 
              />
              <Button size="small" variant="outlined" startIcon={<ImportIcon />} onClick={() => setImportOpen(true)} sx={{ borderRadius: 1.5 }}>
                นำเข้าโรงเรียน (Excel/CSV)
              </Button>
              <Button size="small" variant="outlined" color="success" startIcon={<DownloadIcon />} onClick={handleExportExcel} sx={{ borderRadius: 1.5 }}>
                ส่งออกโรงเรียน (Excel)
              </Button>
              <Button size="small" variant="outlined" color="primary" startIcon={<AutoFixHighIcon />} onClick={handleOpenBatchTextTool} sx={{ borderRadius: 1.5 }}>
                เครื่องมือจัดการข้อความ (Batch Tool)
              </Button>
              <Button size="small" variant="outlined" color="warning" startIcon={<MergeIcon />} onClick={handleOpenDedupModal} sx={{ borderRadius: 1.5 }}>
                ตรวจจับโรงเรียนซ้ำซ้อน
              </Button>
              <Button size="small" variant="outlined" color="info" startIcon={<GroupIcon />} onClick={() => navigate('/dashboard/demand-survey')} sx={{ borderRadius: 1.5 }}>
                สำรวจความต้องการนักศึกษา
              </Button>
            </Box>
          </Grid>

          {/* Group 2: ข้อมูลครูพี่เลี้ยง */}
          <Grid size={{ xs: 12, lg: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, justifyContent: { xs: 'flex-start', lg: 'flex-end' } }}>
              <Chip 
                icon={<PersonIcon sx={{ fontSize: '1rem !important' }} />} 
                label="หมวดหมู่ครูพี่เลี้ยง:" 
                size="small" 
                variant="outlined"
                sx={{ fontWeight: 700, bgcolor: 'background.paper', borderColor: 'secondary.light' }} 
              />
              <Button size="small" variant="outlined" color="secondary" startIcon={<ImportIcon />} onClick={() => setCoTeacherImportOpen(true)} sx={{ borderRadius: 1.5 }}>
                นำเข้าครูพี่เลี้ยง (CSV)
              </Button>
              <Button size="small" variant="outlined" color="secondary" startIcon={<DownloadIcon />} onClick={handleExportCoTeachersExcel} sx={{ borderRadius: 1.5 }}>
                ส่งออกครูพี่เลี้ยง (Excel)
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} sx={{ alignItems: 'center' }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField 
              fullWidth 
              size="small" 
              placeholder="ค้นหาชื่อโรงเรียน, สังกัด, ผู้บริหาร, ครูพี่เลี้ยง หรือนักศึกษา..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 3 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="ภาคเรียน"
              value={selectedSemester}
              onChange={(e) => handleFilterSemesterChange(e.target.value)}
            >
              <MenuItem value="all">ทุกภาคเรียน</MenuItem>
              <MenuItem value="1">ภาคเรียนที่ 1</MenuItem>
              <MenuItem value="2">ภาคเรียนที่ 2</MenuItem>
              <MenuItem value="3">ภาคเรียนฤดูร้อน (3)</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 3 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="ปีการศึกษา"
              value={selectedYear}
              onChange={(e) => handleFilterYearChange(e.target.value)}
            >
              <MenuItem value="all">ทุกปีการศึกษา</MenuItem>
              {availableYears.map((yr) => (
                <MenuItem key={yr} value={yr}>ปีการศึกษา {yr}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 2 }}>
        {selected.length > 0 && (
          <Box sx={{ p: 2, bgcolor: 'error.50', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="subtitle2" color="error.main" sx={{ fontWeight: 'bold' }}>
              เลือกสถานศึกษาแล้ว {selected.length} รายการ
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="outlined" 
                color="primary" 
                startIcon={<AutoFixHighIcon />} 
                onClick={handleOpenBatchTextTool}
                size="small"
                sx={{ bgcolor: 'white', fontWeight: 700 }}
              >
                จัดการข้อความ ({selected.length} แห่ง)
              </Button>
              <Button 
                variant="contained" 
                color="error" 
                startIcon={<DeleteIcon />} 
                onClick={handleBulkDelete}
                size="small"
              >
                ลบรายการที่เลือก
              </Button>
            </Box>
          </Box>
        )}
        <Table sx={{ minWidth: 800 }}>
          <TableHead sx={{ bgcolor: 'grey.100' }}>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selected.length > 0 && selected.length < filteredSchools.length}
                  checked={filteredSchools.length > 0 && selected.length === filteredSchools.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>ชื่อโรงเรียน</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>สังกัด</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>ผู้บริหาร</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>ครูพี่เลี้ยง</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>นักศึกษา</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>ความจุ (คน)</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>การจัดการ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSchools.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((school: any) => {
              const isItemSelected = selected.indexOf(school.id) !== -1;
              return (
                <TableRow 
                  key={school.id} 
                  hover 
                  selected={isItemSelected}
                  onClick={() => handleShowDetails(school)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell padding="checkbox" onClick={(e) => { e.stopPropagation(); handleSelectOne(school.id); }}>
                    <Checkbox checked={isItemSelected} />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SchoolIcon color="primary" />
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{school.name}</Typography>
                    </Box>
                    {school.school_size_level && (
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                        <Chip size="small" variant="outlined" label={SCHOOL_SIZES.find(s => s.value === school.school_size_level)?.shortLabel || school.school_size_level} sx={{ fontSize: '0.65rem', height: 18 }} />
                        {school.distance_from_university_km !== null && school.distance_from_university_km !== undefined && (
                          <Chip size="small" label={`${school.distance_from_university_km} กม.`} sx={{ fontSize: '0.65rem', height: 18 }} />
                        )}
                        {school.has_accommodation === 1 && (
                          <Chip size="small" color="success" label="มีที่พัก" sx={{ fontSize: '0.65rem', height: 18 }} />
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>{school.affiliation}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PersonIcon fontSize="small" color="action" />
                      <Typography variant="caption">
                        {school.principal_first_name ? `${school.principal_prefix}${school.principal_first_name} ${school.principal_last_name}` : '-'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="จำนวนครูพี่เลี้ยงในสถานศึกษา (คลิกแถวเพื่อดูรายชื่อ)">
                      <Chip 
                        label={`${school.coteacher_count || 0} คน`} 
                        size="small" 
                        color={school.coteacher_count > 0 ? "secondary" : "default"} 
                        variant={school.coteacher_count > 0 ? "filled" : "outlined"}
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="จำนวนนักศึกษาฝึกสอนที่สังกัด (คลิกแถวเพื่อดูรายชื่อ)">
                      <Chip 
                        label={`${school.placement_count || 0} คน`} 
                        size="small" 
                        color={school.placement_count > 0 ? "primary" : "default"} 
                        variant={school.placement_count > 0 ? "filled" : "outlined"}
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="จำนวนนักศึกษาที่รองรับได้ทั้งหมด">
                        <Chip label={school.capacity} size="small" color="info" />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <IconButton color="primary" onClick={() => handleOpen(school)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton color="error" onClick={() => handleDelete(school.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
            {filteredSchools.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="textSecondary">ไม่พบข้อมูลโรงเรียน</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={filteredSchools.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="รายการต่อหน้า:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} จาก ${count}`}
        />
      </TableContainer>

      {/* Import Excel / CSV Dialog */}
      <Dialog open={importOpen} onClose={() => setImportOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ImportIcon color="primary" /> นำเข้า / อัปเดตข้อมูลโรงเรียนจากไฟล์ Excel หรือ CSV
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.50', borderRadius: 2, border: '1px solid', borderColor: 'primary.200' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold', color: 'primary.main' }}>
              <HelpIcon fontSize="small" /> รูปแบบและเงื่อนไขการนำเข้าข้อมูล (Excel & CSV):
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1, pl: 1 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>ข้อมูลพื้นฐาน:</Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>• <b>รหัสอ้างอิง (School ID):</b> <i>(แนะนำสำหรับการอัปเดต)</i> หากใส่รหัสเดิมที่ส่งออกจากระบบ จะอัปเดตข้อมูลโรงเรียนเดิมทันที (สามารถแก้ไขชื่อโรงเรียนได้โดยไม่เกิดข้อมูลซ้ำ)</Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>• <b>ชื่อโรงเรียน:</b> ชื่อสถานศึกษา (จำเป็น หากไม่มีรหัสอ้างอิง)</Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>• <b>สังกัด:</b> เช่น สพม.กำแพงเพชร, สพป.ตาก เขต 1</Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>• <b>ที่อยู่:</b> ที่ตั้งของโรงเรียน</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>ข้อมูลผู้บริหาร:</Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>• <b>คำนำหน้าผู้บริหาร:</b> นาย, นาง, นางสาว, ดร.</Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>• <b>ชื่อผู้บริหาร, นามสกุลผู้บริหาร</b></Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>• <b>เบอร์โทรผู้บริหาร:</b> ตัวเลขโทรศัพท์</Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', color: 'secondary.main' }}>ข้อมูลความจุรายสาขา (Optional):</Typography>
                    <Typography variant="caption">
                        เพิ่มคอลัมน์ตามชื่อสาขาวิชาในระบบ (เช่น การประถมศึกษา, ภาษาไทย, ฯลฯ) และใส่ตัวเลขจำนวนนักศึกษาที่รับได้
                    </Typography>
                </Grid>
            </Grid>
            <Box sx={{ mt: 2, display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button 
                    size="small" 
                    variant="contained" 
                    color="success" 
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadExcelTemplate}
                >
                    ดาวน์โหลดเทมเพลต Excel (.xlsx)
                </Button>
                <Button 
                    size="small" 
                    variant="outlined" 
                    color="primary" 
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadTemplate}
                >
                    ดาวน์โหลดเทมเพลต CSV (.csv)
                </Button>
            </Box>
          </Box>

          {importMessage.text && <Alert severity={importMessage.type as any} sx={{ mb: 2 }}>{importMessage.text}</Alert>}

          <Box sx={{ border: '2px dashed #ccc', p: 4, textAlign: 'center', borderRadius: 2, bgcolor: 'grey.50' }}>
            <input
              type="file"
              accept=".csv, .xlsx, .xls"
              style={{ display: 'none' }}
              id="school-file-input"
              onChange={handleFileUpload}
            />
            <label htmlFor="school-file-input">
              <Button variant="outlined" component="span" startIcon={<ImportIcon />} size="large">
                เลือกไฟล์ Excel (.xlsx) หรือ CSV จากเครื่องคอมพิวเตอร์
              </Button>
            </label>
            <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary' }}>
              รองรับทั้งไฟล์ Microsoft Excel (.xlsx, .xls) และ CSV (UTF-8)
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)}>ปิดหน้าต่าง</Button>
        </DialogActions>
      </Dialog>

      {/* Edit/Add Dialog */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          {editingId ? 'แก้ไขข้อมูลโรงเรียน' : 'เพิ่มโรงเรียนเครือข่ายใหม่'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom sx={{ fontWeight: 'bold' }}>
                ข้อมูลพื้นฐานโรงเรียน
              </Typography>
              <TextField
                fullWidth
                label="ชื่อโรงเรียน"
                name="name"
                value={formData.name}
                onChange={handleChange}
                sx={{ mb: 2 }}
                required
              />
              <TextField
                select
                fullWidth
                label="สังกัดสถานศึกษา (ตามเกณฑ์คุรุสภา)"
                value={affiliationType}
                onChange={(e) => {
                  const val = e.target.value;
                  setAffiliationType(val);
                  if (val !== 'อื่นๆ (โปรดระบุ)') {
                    setFormData({ ...formData, affiliation: val });
                    setCustomAffiliationText('');
                  } else {
                    setFormData({ ...formData, affiliation: customAffiliationText });
                  }
                }}
                sx={{ mb: affiliationType === 'อื่นๆ (โปรดระบุ)' ? 1.5 : 2 }}
                required
              >
                {STANDARD_AFFILIATIONS.map((affil) => (
                  <MenuItem key={affil} value={affil}>
                    {affil}
                  </MenuItem>
                ))}
              </TextField>

              {affiliationType === 'อื่นๆ (โปรดระบุ)' && (
                <TextField
                  fullWidth
                  label="ระบุชื่อสังกัดสถานศึกษา"
                  placeholder="เช่น สำนักการศึกษา กรุงเทพมหานคร, สพป.กำแพงเพชร เขต 1"
                  value={customAffiliationText}
                  onChange={(e) => {
                    const val = e.target.value;
                    setCustomAffiliationText(val);
                    setFormData({ ...formData, affiliation: val });
                  }}
                  sx={{ mb: 2 }}
                  required
                  autoFocus
                />
              )}
              <TextField
                fullWidth
                multiline
                rows={3}
                label="ที่อยู่"
                name="address"
                value={formData.address}
                onChange={handleChange}
              />

              <Typography variant="subtitle2" color="primary" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
                ข้อมูลผู้บริหารสถานศึกษา
              </Typography>
              <Grid container spacing={1}>
                <Grid size={{ xs: 3 }}>
                   <TextField
                    select
                    fullWidth
                    label="คำนำหน้า"
                    name="principal_prefix"
                    value={formData.principal_prefix}
                    onChange={handleChange}
                    slotProps={{ select: { native: true } }}
                   >
                     <option value="นาย">นาย</option>
                     <option value="นาง">นาง</option>
                     <option value="นางสาว">นางสาว</option>
                     <option value="ดร.">ดร.</option>
                   </TextField>
                </Grid>
                <Grid size={{ xs: 4.5 }}>
                  <TextField fullWidth label="ชื่อ" name="principal_first_name" value={formData.principal_first_name} onChange={handleChange} />
                </Grid>
                <Grid size={{ xs: 4.5 }}>
                  <TextField fullWidth label="นามสกุล" name="principal_last_name" value={formData.principal_last_name} onChange={handleChange} />
                </Grid>
              </Grid>
              <TextField
                fullWidth
                label="เบอร์โทรศัพท์ผู้บริหาร"
                name="principal_phone"
                value={formData.principal_phone}
                onChange={handleChange}
                sx={{ mt: 2 }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ bgcolor: 'secondary.50', p: 2, borderRadius: 2, border: '1px solid', borderColor: 'secondary.200' }}>
                <Typography variant="subtitle2" color="secondary" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GroupIcon fontSize="small" /> จำนวนนักศึกษาที่รับได้รายสาขาวิชา
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                  ระบุจำนวนคน (0 = ไม่รับในสาขานี้)
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  {MAJORS.map((major) => (
                    <Grid size={{ xs: 6 }} key={major}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label={major}
                        value={formData.major_capacities[major]}
                        onChange={(e) => handleMajorCapacityChange(major, e.target.value)}
                        slotProps={{ htmlInput: { min: 0 } }}
                      />
                    </Grid>
                  ))}
                </Grid>
                <Box sx={{ mt: 3, p: 2, bgcolor: 'secondary.main', color: 'white', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="caption">ยอดรวมความจุทั้งหมด:</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        {Object.values(formData.major_capacities).reduce((a: number, b: any) => a + (parseInt(b) || 0), 0)} คน
                    </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} color="inherit">ยกเลิก</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">บันทึกข้อมูล</Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={detailsOpen} onClose={handleCloseDetails} fullWidth maxWidth="lg">
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <SchoolIcon sx={{ fontSize: 32 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                  {selectedSchoolForDetails?.name}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  สังกัด: {selectedSchoolForDetails?.affiliation || 'ไม่ระบุสังกัด'} | รหัส: {selectedSchoolForDetails?.id?.slice(0, 8)}...
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleCloseDetails} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
          <Tabs 
            value={detailsTab} 
            onChange={(e, val) => setDetailsTab(val)}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab 
              icon={<SupervisorIcon />} 
              iconPosition="start" 
              label={`ครูพี่เลี้ยง (${selectedSchoolForDetails?.co_teachers?.length || 0})`} 
              sx={{ fontWeight: 'bold' }}
            />
            <Tab 
              icon={<SchoolIcon />} 
              iconPosition="start" 
              label="ข้อมูลสถานศึกษา & โควตา" 
              sx={{ fontWeight: 'bold' }}
            />
            <Tab 
              icon={<GroupIcon />} 
              iconPosition="start" 
              label={`นักศึกษาฝึกสอน (${selectedSchoolForDetails?.students?.length || 0})`} 
              sx={{ fontWeight: 'bold' }}
            />
          </Tabs>
        </Box>

        <DialogContent dividers sx={{ p: 3, bgcolor: '#fbfbfb' }}>
          {detailsLoading ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography color="textSecondary">กำลังโหลดข้อมูลสถานศึกษาและครูพี่เลี้ยง...</Typography>
            </Box>
          ) : selectedSchoolForDetails && (
            <>
              {/* Tab 0: Co-Teachers Grouped by Department */}
              {detailsTab === 0 && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Alert severity="info" sx={{ flex: 1 }}>
                      คลิกที่ <b>ชื่อครูพี่เลี้ยง</b> หรือปุ่ม <b>"จัดการ"</b> เพื่อดูรายละเอียด แก้ไขข้อมูลกลุ่มสาระ รีเซ็ตรหัสผ่าน หรือเปลี่ยนสถานะบัญชี
                    </Alert>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip 
                        label={`รวมครูพี่เลี้ยงทั้งหมด ${selectedSchoolForDetails.co_teachers?.length || 0} คน`} 
                        color="primary" 
                        sx={{ fontWeight: 'bold', height: 36, px: 1 }} 
                      />
                      <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        startIcon={<PersonAddIcon />}
                        onClick={() => handleOpenCreateCoTeacher(selectedSchoolForDetails.id)}
                        sx={{ height: 36 }}
                      >
                        เพิ่มครูพี่เลี้ยงในโรงเรียนนี้
                      </Button>
                    </Box>
                  </Box>

                  {(!selectedSchoolForDetails.co_teachers || selectedSchoolForDetails.co_teachers.length === 0) ? (
                    <Alert severity="warning" sx={{ my: 3 }}>
                      ยังไม่พบข้อมูลครูพี่เลี้ยงที่สังกัดโรงเรียนนี้ในระบบ
                    </Alert>
                  ) : (
                    <Stack spacing={2.5}>
                      {Object.entries(
                        (selectedSchoolForDetails.co_teachers || []).reduce((acc: any, ct: any) => {
                          const dept = ct.position?.trim() || 'ไม่ระบุกลุ่มสาระ';
                          if (!acc[dept]) acc[dept] = [];
                          acc[dept].push(ct);
                          return acc;
                        }, {})
                      ).map(([dept, teachers]: [string, any]) => (
                        <Paper key={dept} variant="outlined" sx={{ overflow: 'hidden', borderRadius: 2 }}>
                          <Box sx={{ 
                            px: 2, 
                            py: 1.2, 
                            bgcolor: 'grey.100', 
                            borderBottom: '1px solid', 
                            borderColor: 'divider',
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between' 
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CategoryIcon color="primary" fontSize="small" />
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
                                กลุ่มสาระการเรียนรู้ / สาขาวิชา: {dept}
                              </Typography>
                            </Box>
                            <Chip 
                              label={`${teachers.length} ท่าน`} 
                              size="small" 
                              color="secondary" 
                              sx={{ fontWeight: 'bold' }} 
                            />
                          </Box>

                          <TableContainer>
                            <Table size="small">
                              <TableHead sx={{ bgcolor: 'grey.50' }}>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 'bold' }}>ชื่อ - นามสกุลครูพี่เลี้ยง</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }}>บัญชีผู้ใช้</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }}>เบอร์โทรศัพท์</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }}>อีเมล</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>นศ. ที่ดูแล</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>สถานะ</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>การจัดการ</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {teachers.map((ct: any) => (
                                  <TableRow key={ct.id} hover>
                                    <TableCell>
                                      <Button 
                                        variant="text" 
                                        color="primary" 
                                        size="small"
                                        startIcon={<PersonIcon />}
                                        onClick={(e) => handleOpenManageCoTeacher(ct, e)}
                                        sx={{ 
                                          fontWeight: 'bold', 
                                          textAlign: 'left',
                                          textDecoration: 'underline',
                                          p: 0.5,
                                          '&:hover': { bgcolor: 'primary.50' }
                                        }}
                                      >
                                        {ct.prefix}{ct.first_name} {ct.last_name}
                                      </Button>
                                    </TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                      @{ct.username || '-'}
                                    </TableCell>
                                    <TableCell>
                                      {ct.phone_number ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <PhoneIcon fontSize="inherit" color="action" />
                                          <Typography variant="body2">{ct.phone_number}</Typography>
                                        </Box>
                                      ) : '-'}
                                    </TableCell>
                                    <TableCell>
                                      {ct.email ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <EmailIcon fontSize="inherit" color="action" />
                                          <Typography variant="body2">{ct.email}</Typography>
                                        </Box>
                                      ) : '-'}
                                    </TableCell>
                                    <TableCell align="center">
                                      <Chip 
                                        label={`${ct.student_count || 0} คน`} 
                                        size="small" 
                                        color={ct.student_count > 0 ? "primary" : "default"} 
                                      />
                                    </TableCell>
                                    <TableCell align="center">
                                      {ct.is_active === 1 || ct.is_active === true ? (
                                        <Chip label="ใช้งานปกติ" color="success" size="small" variant="outlined" />
                                      ) : (
                                        <Chip label="ปิดใช้งาน" color="default" size="small" variant="outlined" />
                                      )}
                                    </TableCell>
                                    <TableCell align="center">
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<EditIcon />}
                                        onClick={(e) => handleOpenManageCoTeacher(ct, e)}
                                      >
                                        จัดการ
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Box>
              )}

              {/* Tab 1: General Info & Capacity */}
              {detailsTab === 1 && (
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle1" color="primary" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                      ข้อมูลทั่วไปของโรงเรียน
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>ชื่อโรงเรียน</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{selectedSchoolForDetails.name}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>สังกัด</Typography>
                        <Typography variant="body1">{selectedSchoolForDetails.affiliation || '-'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>ที่อยู่</Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{selectedSchoolForDetails.address || '-'}</Typography>
                      </Box>
                      
                      <Divider sx={{ my: 1 }} />
                      
                      <Typography variant="subtitle1" color="primary" gutterBottom sx={{ fontWeight: 'bold' }}>
                        ข้อมูลผู้บริหาร
                      </Typography>
                      <Box>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>ชื่อผู้บริหาร</Typography>
                        <Typography variant="body1">
                          {selectedSchoolForDetails.principal_first_name 
                            ? `${selectedSchoolForDetails.principal_prefix}${selectedSchoolForDetails.principal_first_name} ${selectedSchoolForDetails.principal_last_name}` 
                            : '-'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>เบอร์โทรศัพท์ผู้บริหาร</Typography>
                        <Typography variant="body1" color="primary.main" sx={{ fontWeight: 'bold' }}>
                          {selectedSchoolForDetails.principal_phone || '-'}
                        </Typography>
                      </Box>

                      <Divider sx={{ my: 1 }} />

                      <Typography variant="subtitle1" color="primary" gutterBottom sx={{ fontWeight: 'bold' }}>
                        ข้อมูลความพร้อมและบริบทสถานศึกษา
                      </Typography>
                      <Grid container spacing={1.5}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>ขนาดสถานศึกษา</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {selectedSchoolForDetails.school_size_level 
                              ? (SCHOOL_SIZES.find(s => s.value === selectedSchoolForDetails.school_size_level)?.shortLabel || selectedSchoolForDetails.school_size_level)
                              : '-'}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>ระยะทางจาก มรภ.กำแพงเพชร</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {selectedSchoolForDetails.distance_from_university_km !== null && selectedSchoolForDetails.distance_from_university_km !== undefined
                              ? `${selectedSchoolForDetails.distance_from_university_km} กม.`
                              : '-'}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>ที่พักสำหรับนักศึกษา</Typography>
                          <Typography variant="body2">
                            {selectedSchoolForDetails.has_accommodation === 1 ? 'มีที่พัก' : 'ไม่มีที่พัก'}
                            {selectedSchoolForDetails.accommodation_details ? ` (${selectedSchoolForDetails.accommodation_details})` : ''}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>อาหารกลางวัน</Typography>
                          <Typography variant="body2">
                            {selectedSchoolForDetails.has_lunch === 1 ? 'มีอาหารกลางวัน' : 'ไม่มี'}
                          </Typography>
                        </Grid>
                        {selectedSchoolForDetails.welfare_notes && (
                          <Grid size={{ xs: 12 }}>
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>สวัสดิการอื่นๆ</Typography>
                            <Typography variant="body2">{selectedSchoolForDetails.welfare_notes}</Typography>
                          </Grid>
                        )}
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>จำนวนครูทั้งหมด</Typography>
                          <Typography variant="body2">{selectedSchoolForDetails.teacher_count_total ? `${selectedSchoolForDetails.teacher_count_total} คน` : '-'}</Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>จำนวนนักเรียนทั้งหมด</Typography>
                          <Typography variant="body2">{selectedSchoolForDetails.student_count_total ? `${selectedSchoolForDetails.student_count_total} คน` : '-'}</Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box sx={{ bgcolor: 'secondary.50', p: 3, borderRadius: 2, border: '1px solid', borderColor: 'secondary.200', height: '100%' }}>
                      <Typography variant="subtitle1" color="secondary" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <GroupIcon fontSize="small" /> โควตานักศึกษาที่เปิดรับรายสาขา
                      </Typography>
                      <Divider sx={{ mb: 2 }} />

                      {selectedSchoolForDetails.major_capacities && 
                      Object.values(selectedSchoolForDetails.major_capacities).some((c: any) => (parseInt(c) || 0) > 0) ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {MAJORS.map((major) => {
                            const cap = parseInt(selectedSchoolForDetails.major_capacities?.[major]) || 0;
                            if (cap === 0) return null;
                            return (
                              <Chip 
                                key={major} 
                                label={`${major}: ${cap} คน`} 
                                color="secondary" 
                                variant="outlined" 
                                sx={{ fontWeight: 'bold' }}
                              />
                            );
                          })}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                          ไม่มีการเปิดรับนักศึกษาในแต่ละสาขา
                        </Typography>
                      )}

                      <Box sx={{ mt: 4, p: 2, bgcolor: 'secondary.main', color: 'white', borderRadius: 1, textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ display: 'block' }}>โควตานักศึกษาที่รองรับได้ทั้งหมด:</Typography>
                        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                          {selectedSchoolForDetails.capacity || 0} คน
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              )}

              {/* Tab 2: Students in this school */}
              {detailsTab === 2 && (
                <Box>
                  {(!selectedSchoolForDetails.students || selectedSchoolForDetails.students.length === 0) ? (
                    <Alert severity="info" sx={{ my: 2 }}>
                      ยังไม่มีนักศึกษาฝึกประสบการณ์วิชาชีพที่สังกัดสถานศึกษานี้ในขณะนี้
                    </Alert>
                  ) : (
                    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                      <TableContainer>
                        <Table size="small">
                          <TableHead sx={{ bgcolor: 'grey.100' }}>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold' }}>รหัสนักศึกษา</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>ชื่อ - นามสกุล</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>สาขาวิชา</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold' }}>ภาค/ปีการศึกษา</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>ครูพี่เลี้ยงที่ดูแล</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>เบอร์โทรศัพท์</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold' }}>การดำเนินการ</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedSchoolForDetails.students.map((st: any) => (
                              <TableRow key={st.placement_id} hover>
                                <TableCell sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                                  {st.student_code}
                                </TableCell>
                                <TableCell>
                                  {st.prefix}{st.first_name} {st.last_name}
                                </TableCell>
                                <TableCell>
                                  <Chip label={st.major} size="small" color="primary" variant="outlined" />
                                </TableCell>
                                <TableCell align="center">
                                  {st.semester}/{st.academic_year}
                                </TableCell>
                                <TableCell>
                                  {st.co_teacher_first_name ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <PersonIcon fontSize="small" color="secondary" />
                                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                        {st.co_teacher_prefix}{st.co_teacher_first_name} {st.co_teacher_last_name}
                                      </Typography>
                                      {st.co_teacher_position && (
                                        <Typography variant="caption" color="textSecondary">
                                          ({st.co_teacher_position})
                                        </Typography>
                                      )}
                                    </Box>
                                  ) : (
                                    <Chip label="ยังไม่ระบุครูพี่เลี้ยง" size="small" color="warning" />
                                  )}
                                </TableCell>
                                <TableCell>
                                  {st.phone_number || '-'}
                                </TableCell>
                                <TableCell align="center">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<TransferIcon />}
                                    onClick={() => handleOpenRelocateStudent(st)}
                                    sx={{ whiteSpace: 'nowrap' }}
                                  >
                                    ย้ายโรงเรียน / พี่เลี้ยง
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  )}
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDetails} variant="contained" color="inherit">
            ปิดหน้าต่าง
          </Button>
        </DialogActions>
      </Dialog>

      {/* Co-Teacher Management Modal */}
      <Dialog 
        open={coTeacherManageOpen} 
        onClose={() => setCoTeacherManageOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'secondary.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ManageIcon /> จัดการข้อมูลครูพี่เลี้ยง
        </DialogTitle>
        <DialogContent dividers>
          {selectedCoTeacher && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'secondary.main' }}>
                    {selectedCoTeacher.first_name?.charAt(0) || 'C'}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {coTeacherFormData.prefix || ''}{coTeacherFormData.first_name} {coTeacherFormData.last_name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      บัญชีผู้ใช้: <b>@{coTeacherFormData.username || selectedCoTeacher.username || '-'}</b> | สังกัด: {selectedSchoolForDetails?.name}
                    </Typography>
                  </Box>
                </Box>
                <Chip 
                  label={coTeacherFormData.is_active ? "เปิดใช้งาน (Active)" : "ระงับการใช้งาน"} 
                  color={coTeacherFormData.is_active ? "success" : "default"} 
                  size="small" 
                />
              </Paper>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="ชื่อบัญชีผู้ใช้ (Username)"
                    value={coTeacherFormData.username || ''}
                    onChange={(e) => setCoTeacherFormData({ ...coTeacherFormData, username: e.target.value })}
                    placeholder="เช่น somchai_k"
                    helperText="ชื่อบัญชีผู้ใช้สำหรับเข้าสู่ระบบของครูพี่เลี้ยง (สามารถแก้ไขได้)"
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Autocomplete
                    freeSolo
                    options={PREFIX_OPTIONS}
                    value={coTeacherFormData.prefix || ''}
                    onChange={(e, val) => setCoTeacherFormData({ ...coTeacherFormData, prefix: val || '' })}
                    onInputChange={(e, val) => setCoTeacherFormData({ ...coTeacherFormData, prefix: val })}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="คำนำหน้าชื่อ"
                        placeholder="เลือกหรือพิมพ์เอง..."
                        helperText="เลือกหรือพิมพ์เพิ่มเติมได้"
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="ชื่อ"
                    value={coTeacherFormData.first_name}
                    onChange={(e) => setCoTeacherFormData({ ...coTeacherFormData, first_name: e.target.value })}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="นามสกุล"
                    value={coTeacherFormData.last_name}
                    onChange={(e) => setCoTeacherFormData({ ...coTeacherFormData, last_name: e.target.value })}
                    required
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Autocomplete
                    options={schools}
                    getOptionLabel={(option: any) => `${option.name} (${option.affiliation || 'ไม่ระบุสังกัด'})`}
                    value={schools.find((s: any) => s.id === coTeacherFormData.school_id) || null}
                    onChange={(e, val: any) => setCoTeacherFormData({ ...coTeacherFormData, school_id: val?.id || '' })}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="สถานศึกษาที่สังกัด (โยกย้ายโรงเรียน)"
                        helperText="สามารถเปลี่ยนโรงเรียนเพื่อโยกย้ายครูพี่เลี้ยงไปยังสถานศึกษาอื่นได้"
                        required
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Autocomplete
                    freeSolo
                    options={MAJORS}
                    value={coTeacherFormData.position || ''}
                    onChange={(e, val) => setCoTeacherFormData({ ...coTeacherFormData, position: val || '' })}
                    onInputChange={(e, val) => setCoTeacherFormData({ ...coTeacherFormData, position: val })}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        size="small" 
                        label="กลุ่มสาระการเรียนรู้ / สาขาวิชาที่สอน" 
                        placeholder="เช่น ภาษาไทย, คณิตศาสตร์, วิทยาศาสตร์ทั่วไป..." 
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="เบอร์โทรศัพท์"
                    value={coTeacherFormData.phone_number}
                    onChange={(e) => setCoTeacherFormData({ ...coTeacherFormData, phone_number: e.target.value })}
                    placeholder="08xxxxxxxx"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="อีเมล"
                    value={coTeacherFormData.email}
                    onChange={(e) => setCoTeacherFormData({ ...coTeacherFormData, email: e.target.value })}
                    placeholder="example@email.com"
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={coTeacherFormData.is_active}
                        onChange={(e) => setCoTeacherFormData({ ...coTeacherFormData, is_active: e.target.checked })}
                        color="success"
                      />
                    }
                    label={
                      <Typography variant="body2">
                        เปิดใช้งานบัญชีผู้ใช้ (ครูพี่เลี้ยงสามารถล็อกอินเข้าสู่ระบบได้)
                      </Typography>
                    }
                  />
                </Grid>
              </Grid>

              <Divider />

              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1.5, border: '1px dashed', borderColor: 'grey.400' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary' }}>
                  การจัดการความปลอดภัยและสถานะ
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    startIcon={<ResetIcon />}
                    onClick={handleResetCoTeacherPassword}
                    disabled={coTeacherSaving}
                  >
                    รีเซ็ตรหัสผ่าน
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={handleDeleteCoTeacher}
                    disabled={coTeacherSaving}
                  >
                    ลบครูพี่เลี้ยง (ย้ายลงถังขยะ)
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCoTeacherManageOpen(false)} color="inherit" disabled={coTeacherSaving}>
            ปิดหน้าต่าง
          </Button>
          <Button 
            onClick={handleSaveCoTeacher} 
            variant="contained" 
            color="primary" 
            disabled={coTeacherSaving}
          >
            {coTeacherSaving ? 'กำลังบันทึก...' : 'บันทึกการเปลี่ยนแปลง'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Schools Modal */}
      <Dialog open={dedupOpen} onClose={() => setDedupOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ bgcolor: 'warning.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon /> ตรวจสอบและรวมข้อมูลโรงเรียนที่ซ้ำซ้อน (School Deduplication & Merge)
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip 
              label={`พบซ้ำ ${duplicateGroups.length} กลุ่ม`} 
              size="small" 
              sx={{ bgcolor: 'white', color: 'warning.dark', fontWeight: 800 }} 
            />
            {dismissedGroups.length > 0 && (
              <Chip 
                label={`ไม่ซ้ำ ${dismissedGroups.length} กลุ่ม`} 
                size="small" 
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700 }} 
              />
            )}
          </Box>
        </DialogTitle>
        <Box sx={{ px: 3, pt: 1, bgcolor: '#f1f5f9', borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={dedupTab} onChange={(_, val) => setDedupTab(val)}>
            <Tab label={`กลุ่มที่ตรวจพบซ้ำ (${duplicateGroups.length})`} sx={{ fontWeight: 700 }} />
            <Tab label={`รายการที่ระบุว่าไม่ซ้ำ (${dismissedGroups.length})`} sx={{ fontWeight: 700 }} />
          </Tabs>
        </Box>
        <DialogContent dividers sx={{ bgcolor: '#f8fafc' }}>
          {dedupLoading ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <LinearProgress sx={{ mb: 2 }} />
              <Typography sx={{ fontWeight: 600 }}>กำลังประมวลผลข้อมูลโรงเรียนซ้ำซ้อนในระบบ...</Typography>
            </Box>
          ) : dedupTab === 0 ? (
            duplicateGroups.length === 0 ? (
              <Alert severity="success" sx={{ my: 2 }}>
                ยอดเยี่ยม! ไม่พบรายชื่อโรงเรียนที่ซ้ำซ้อนในระบบ หรือกลุ่มที่เคยตรวจพบได้รับการจัดการและระบุสถานะแล้ว 100%
              </Alert>
            ) : (
              <Box>
                <Alert severity="warning" sx={{ mb: 3 }}>
                  ระบบตรวจพบรายชื่อโรงเรียนที่ชื่อใกล้เคียงหรือซ้ำซ้อนกันทั้งหมด <b>{duplicateGroups.length}</b> กลุ่ม 
                  <br />
                  • หากเป็นโรงเรียนเดียวกัน: เลือกแถวที่ต้องการให้เป็น <b>"ข้อมูลหลัก"</b> และคลิกปุ่ม <b>"ใช้รายการนี้เป็นหลัก"</b> ระบบจะรวมนักศึกษาและครูพี่เลี้ยงทั้งหมดเข้าด้วยกัน
                  <br />
                  • หากเป็นคนละโรงเรียน: คลิกปุ่ม <b>"ไม่ซ้ำ (ยกเว้นกลุ่มนี้)"</b> ระบบจะไม่นำกลุ่มนี้มาแจ้งเตือนซ้ำอีก
                </Alert>

                {duplicateGroups.map((group: any, idx: number) => {
                  const totalGroupStudents = group.schools.reduce((acc: number, sc: any) => acc + (Number(sc.placement_count) || 0), 0);
                  const totalGroupTeachers = group.schools.reduce((acc: number, sc: any) => acc + (Number(sc.coteacher_count) || 0), 0);

                  return (
                    <Paper key={idx} variant="outlined" sx={{ p: 2.5, mb: 2.5, bgcolor: '#ffffff', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'primary.dark' }}>
                          {idx + 1}. กลุ่มชื่อสถานศึกษา: "{group.name}" (พบข้อมูล {group.schools.length} รายการ)
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip 
                            size="small" 
                            color="primary" 
                            variant="outlined" 
                            label={`นักศึกษารวม: ${totalGroupStudents} คน`} 
                            sx={{ fontWeight: 700 }}
                          />
                          <Chip 
                            size="small" 
                            color="secondary" 
                            variant="outlined" 
                            label={`ครูพี่เลี้ยงรวม: ${totalGroupTeachers} คน`} 
                            sx={{ fontWeight: 700 }}
                          />
                          <Tooltip title="หากพิจารณาแล้วว่า รายชื่อโรงเรียนในกลุ่มนี้ไม่ใช่โรงเรียนเดียวกัน ให้คลิกปุ่มนี้เพื่อยกเว้นจากการตรวจสอบ">
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              startIcon={<CheckCircleIcon />}
                              onClick={() => handleDismissGroup(group)}
                              disabled={dedupLoading}
                              sx={{ fontWeight: 700, borderRadius: 1.5, textTransform: 'none', px: 1.5 }}
                            >
                              ไม่ซ้ำ (ยกเว้นกลุ่มนี้)
                            </Button>
                          </Tooltip>
                        </Box>
                      </Box>

                      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
                        <Table size="small">
                          <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 800 }}>ชื่อสถานศึกษา (School Name)</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>รหัสอ้างอิง (ID)</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>สังกัด (Affiliation)</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>ที่อยู่</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 800 }}>นักศึกษาที่สังกัด</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 800 }}>ครูพี่เลี้ยง</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 800 }}>ดำเนินการ</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {group.schools.map((s: any) => (
                              <TableRow key={s.id} hover>
                                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>
                                  {s.name}
                                </TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: 'text.secondary' }}>
                                  <Tooltip title={s.id}>
                                    <span>{s.id.slice(0, 8)}...</span>
                                  </Tooltip>
                                </TableCell>
                                <TableCell>{s.affiliation || '-'}</TableCell>
                                <TableCell sx={{ maxWidth: 200, fontSize: '0.85rem', color: 'text.secondary' }}>
                                  {s.address || '-'}
                                </TableCell>
                                <TableCell align="center">
                                  <Chip 
                                    label={`${s.placement_count} คน`} 
                                    size="small" 
                                    color={s.placement_count > 0 ? "primary" : "default"} 
                                    sx={{ fontWeight: 700 }}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Chip 
                                    label={`${s.coteacher_count} คน`} 
                                    size="small" 
                                    color={s.coteacher_count > 0 ? "secondary" : "default"} 
                                    sx={{ fontWeight: 700 }}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Tooltip title="เลือกใช้โรงเรียนนี้เป็นข้อมูลหลัก โดยระบบจะย้ายนักศึกษาและครูพี่เลี้ยงจากโรงเรียนอื่นในกลุ่มนี้มารวมกันทั้งหมด">
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="warning"
                                      startIcon={<MergeIcon />}
                                      onClick={() => handleMergeGroup(group, s.id)}
                                      disabled={dedupLoading}
                                      sx={{ fontWeight: 700, borderRadius: 1.5, textTransform: 'none', whiteSpace: 'nowrap' }}
                                    >
                                      ใช้รายการนี้เป็นหลัก
                                    </Button>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  );
                })}
              </Box>
            )
          ) : (
            // dedupTab === 1: Dismissed groups list
            <Box>
              <Alert severity="info" sx={{ mb: 2.5 }}>
                รายการด้านล่างนี้คือกลุ่มโรงเรียนที่ Admin/Staff เคยระบุว่า <b>"ไม่ซ้ำ"</b> ระบบจึงยกเว้นไม่นำมาแสดงในการตรวจจับข้อมูลซ้ำซ้อน หากต้องการให้ระบบกลับมาตรวจสอบอีกครั้ง สามารถกดปุ่ม <b>"ยกเลิกการยกเว้น"</b> ได้ทันที
              </Alert>

              {dismissedGroups.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', bgcolor: '#ffffff', borderRadius: 2 }}>
                  <Typography color="text.secondary">
                    ยังไม่มีรายการโรงเรียนที่ถูกระบุว่าไม่ซ้ำ
                  </Typography>
                </Paper>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 800 }}>ลำดับ</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>ชื่อกลุ่มโรงเรียนที่ยกเว้น</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>เหตุผล/หมายเหตุ</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>ผู้บันทึก</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>วันที่ดำเนินการ</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 800 }}>การจัดการ</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dismissedGroups.map((d: any, idx: number) => (
                        <TableRow key={d.key || idx} hover>
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>
                            {d.name || d.key}
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                            {d.reason || 'ระบุว่าไม่ใช่ข้อมูลซ้ำซ้อน'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.85rem' }}>{d.dismissed_by || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.85rem' }}>
                            {d.dismissed_at ? new Date(d.dismissed_at).toLocaleString('th-TH') : '-'}
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              onClick={() => handleRestoreDismissedGroup(d)}
                              disabled={dedupLoading}
                              sx={{ fontWeight: 700, borderRadius: 1.5, textTransform: 'none' }}
                            >
                              ยกเลิกการยกเว้น
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, px: 3 }}>
          <Button onClick={() => setDedupOpen(false)} color="inherit" sx={{ fontWeight: 600 }}>
            ปิดหน้าต่าง
          </Button>
        </DialogActions>
      </Dialog>

      {/* Batch School Text & Affiliation Tool Modal */}
      <Dialog 
        open={batchModalOpen} 
        onClose={() => !batchSaving && setBatchModalOpen(false)} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.dark', 
          color: 'white', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          py: 1.5,
          px: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AutoFixHighIcon />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.15rem' }}>
                เครื่องมือจัดการข้อความและข้อมูลโรงเรียนแบบกลุ่ม (Batch School Tool)
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                ตัดคำนำหน้า/ต่อท้าย ค้นหาและแทนที่ข้อความ หรือกำหนดสังกัดใหม่พร้อมกันหลายโรงเรียน
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={() => setBatchModalOpen(false)} disabled={batchSaving} sx={{ color: 'white' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3, bgcolor: '#f8fafc' }}>
          {/* Scope Selection Card */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2.5, borderRadius: 2, bgcolor: '#ffffff' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <TuneIcon fontSize="small" color="primary" /> 1. ขอบเขตโรงเรียนที่ต้องการดำเนินการ (Target Scope):
            </Typography>
            <RadioGroup
              row
              value={batchScope}
              onChange={(e) => setBatchScope(e.target.value as any)}
            >
              <FormControlLabel 
                value="SELECTED" 
                control={<Radio size="small" />} 
                label={`เฉพาะโรงเรียนที่เลือกไว้ (${selected.length} แห่ง)`}
                disabled={selected.length === 0}
              />
              <FormControlLabel 
                value="FILTERED" 
                control={<Radio size="small" />} 
                label={`โรงเรียนตามผลการค้นหาปัจจุบัน (${filteredSchools.length} แห่ง)`}
              />
              <FormControlLabel 
                value="ALL" 
                control={<Radio size="small" />} 
                label={`โรงเรียนทั้งหมดในระบบ (${schools.length} แห่ง)`}
              />
            </RadioGroup>
          </Paper>

          {/* Operation Tabs */}
          <Paper variant="outlined" sx={{ borderRadius: 2, bgcolor: '#ffffff', mb: 2.5, overflow: 'hidden' }}>
            <Tabs 
              value={batchTab} 
              onChange={(_, val) => setBatchTab(val)}
              variant="fullWidth"
              sx={{ 
                bgcolor: '#f1f5f9', 
                borderBottom: '1px solid #e2e8f0',
                '& .MuiTab-root': { fontWeight: 700, py: 1.5 }
              }}
            >
              <Tab icon={<ContentCutIcon />} iconPosition="start" label="1. ตัดคำนำหน้า / คำต่อท้าย (Trim)" />
              <Tab icon={<FindReplaceIcon />} iconPosition="start" label="2. ค้นหาและแทนที่ข้อความ (Find & Replace)" />
              <Tab icon={<CategoryIcon />} iconPosition="start" label="3. กำหนดสังกัดใหม่พร้อมกัน (Set Affiliation)" />
            </Tabs>

            <Box sx={{ p: 2.5 }}>
              {/* Tab 0: Trim Prefix/Suffix */}
              {batchTab === 0 && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    ลบคำนำหน้า (เช่น "โรงเรียน", "รร.") หรือคำต่อท้ายที่ซ้ำซ้อนออกจากชื่อสถานศึกษาอัตโนมัติ
                  </Typography>

                  <Grid container spacing={2} sx={{ alignItems: 'center', mb: 2 }}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="รูปแบบการตัดข้อความ"
                        value={trimType}
                        onChange={(e) => setTrimType(e.target.value as any)}
                      >
                        <MenuItem value="PREFIX">ตัดคำนำหน้า (Leading Prefix)</MenuItem>
                        <MenuItem value="SUFFIX">ตัดคำต่อท้าย (Trailing Suffix)</MenuItem>
                        <MenuItem value="TRIM_SPACES">ตัดเฉพาะช่องว่างหน้า-หลัง (Trim Spaces)</MenuItem>
                      </TextField>
                    </Grid>

                    {trimType !== 'TRIM_SPACES' && (
                      <Grid size={{ xs: 12, md: 8 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="ข้อความที่ต้องการตัดออก"
                          placeholder="เช่น โรงเรียน หรือ รร."
                          value={trimText}
                          onChange={(e) => setTrimText(e.target.value)}
                        />
                      </Grid>
                    )}
                  </Grid>

                  {trimType !== 'TRIM_SPACES' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                        คำแนะนำลัด (Presets):
                      </Typography>
                      {['โรงเรียน', 'รร.', 'ร.ร.', 'เทศบาล', 'อนุบาล'].map(preset => (
                        <Chip
                          key={preset}
                          label={`"${preset}"`}
                          size="small"
                          clickable
                          variant={trimText === preset ? 'filled' : 'outlined'}
                          color={trimText === preset ? 'primary' : 'default'}
                          onClick={() => { setTrimText(preset); setTrimType('PREFIX'); }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {/* Tab 1: Find & Replace */}
              {batchTab === 1 && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    ค้นหาคำที่สะกดผิด หรือต้องการเปลี่ยนชื่อในฟิลด์ที่เลือก แล้วแทนที่ด้วยคำใหม่
                  </Typography>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="คอลัมน์ที่ต้องการค้นหา"
                        value={targetField}
                        onChange={(e) => setTargetField(e.target.value as any)}
                      >
                        <MenuItem value="name">ชื่อโรงเรียน (School Name)</MenuItem>
                        <MenuItem value="affiliation">สังกัด (Affiliation)</MenuItem>
                        <MenuItem value="address">ที่อยู่ (Address)</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="คำที่ต้องการค้นหา (Find)"
                        placeholder="พิมพ์คำที่ต้องการค้นหา..."
                        value={findText}
                        onChange={(e) => setFindText(e.target.value)}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="แทนที่ด้วย (Replace with)"
                        placeholder="คำใหม่ (เว้นว่างหากต้องการลบออก)"
                        value={replaceText}
                        onChange={(e) => setReplaceText(e.target.value)}
                      />
                    </Grid>
                  </Grid>

                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={caseSensitive}
                        onChange={(e) => setCaseSensitive(e.target.checked)}
                      />
                    }
                    label={<Typography variant="caption">ตรงตามตัวพิมพ์ใหญ่-เล็ก (Case-sensitive)</Typography>}
                  />
                </Box>
              )}

              {/* Tab 2: Batch Set Affiliation */}
              {batchTab === 2 && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    เปลี่ยนข้อมูลสังกัดของโรงเรียนในขอบเขตที่เลือกเป็นสังกัดเดียวกันทั้งหมด
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="เลือกสังกัดมาตรฐาน"
                        value={batchAffiliationChoice}
                        onChange={(e) => setBatchAffiliationChoice(e.target.value)}
                      >
                        <MenuItem value="">-- เลือกสังกัด --</MenuItem>
                        {STANDARD_AFFILIATIONS.map(aff => (
                          <MenuItem key={aff} value={aff}>{aff}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    {batchAffiliationChoice === 'อื่นๆ (โปรดระบุ)' && (
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="ระบุสังกัดสถานศึกษา"
                          placeholder="เช่น สำนักงานเขตพื้นที่การศึกษามัธยมศึกษากำแพงเพชร"
                          value={customBatchAffiliation}
                          onChange={(e) => setCustomBatchAffiliation(e.target.value)}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
            </Box>
          </Paper>

          {/* Live Preview Section */}
          {(() => {
            const previewList = getComputedBatchPreview();
            const changedList = previewList.filter(p => p.hasChanged);
            const activeChangedCount = changedList.filter(p => !excludedBatchIds.includes(p.id)).length;

            return (
              <Paper variant="outlined" sx={{ borderRadius: 2, bgcolor: '#ffffff', overflow: 'hidden' }}>
                <Box sx={{ 
                  p: 2, 
                  bgcolor: changedList.length > 0 ? '#ecfdf5' : '#f8fafc', 
                  borderBottom: '1px solid #e2e8f0',
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon color={changedList.length > 0 ? 'success' : 'disabled'} fontSize="small" />
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: changedList.length > 0 ? 'success.dark' : 'text.secondary' }}>
                      ผลการวิเคราะห์ตัวอย่าง (Live Preview): พบการเปลี่ยนแปลง {changedList.length} แห่ง จากทั้งหมด {previewList.length} แห่ง
                    </Typography>
                  </Box>
                  {changedList.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip 
                        size="small" 
                        color="primary" 
                        label={`เลือกเพื่อบันทึก: ${activeChangedCount} แห่ง`} 
                        sx={{ fontWeight: 700 }}
                      />
                      <Button 
                        size="small" 
                        onClick={() => {
                          if (excludedBatchIds.length === 0) {
                            setExcludedBatchIds(changedList.map(p => p.id));
                          } else {
                            setExcludedBatchIds([]);
                          }
                        }}
                      >
                        {excludedBatchIds.length === 0 ? 'ยกเลิกเลือกทั้งหมด' : 'เลือกทั้งหมด'}
                      </Button>
                    </Box>
                  )}
                </Box>

                {changedList.length === 0 ? (
                  <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography variant="body2">
                      ยังไม่มีรายการที่เข้าเงื่อนไขการเปลี่ยนแปลง หรือยังไม่ได้ระบุคำค้นหา/คำนำหน้า
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer sx={{ maxHeight: 380 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                          <TableCell width={50} align="center">ใช้</TableCell>
                          <TableCell width={60}>ที่</TableCell>
                          <TableCell width="35%">ชื่อโรงเรียนเดิม ➔ ชื่อใหม่</TableCell>
                          <TableCell width="35%">สังกัดเดิม ➔ สังกัดใหม่</TableCell>
                          <TableCell width="22%">ที่อยู่</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {changedList.map((item, idx) => {
                          const isExcluded = excludedBatchIds.includes(item.id);
                          const nameDiff = item.currentName !== item.newName;
                          const affDiff = item.currentAffiliation !== item.newAffiliation;
                          const addrDiff = item.currentAddress !== item.newAddress;

                          return (
                            <TableRow 
                              key={item.id} 
                              sx={{ 
                                bgcolor: isExcluded ? 'transparent' : 'rgba(16, 185, 129, 0.04)',
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.03)' }
                              }}
                            >
                              <TableCell align="center">
                                <Checkbox
                                  size="small"
                                  checked={!isExcluded}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setExcludedBatchIds(excludedBatchIds.filter(id => id !== item.id));
                                    } else {
                                      setExcludedBatchIds([...excludedBatchIds, item.id]);
                                    }
                                  }}
                                />
                              </TableCell>
                              <TableCell>{idx + 1}</TableCell>
                              <TableCell>
                                {nameDiff ? (
                                  <Box>
                                    <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary', display: 'block' }}>
                                      {item.currentName}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>
                                      {item.newName}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="body2">{item.currentName}</Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {affDiff ? (
                                  <Box>
                                    <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary', display: 'block' }}>
                                      {item.currentAffiliation || '-'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>
                                      {item.newAffiliation}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="body2">{item.currentAffiliation || '-'}</Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {addrDiff ? (
                                  <Box>
                                    <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary', display: 'block' }}>
                                      {item.currentAddress || '-'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>
                                      {item.newAddress}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="caption" color="text.secondary">{item.currentAddress || '-'}</Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            );
          })()}
        </DialogContent>

        <DialogActions sx={{ p: 2, px: 3, justifyContent: 'space-between', bgcolor: '#f8fafc' }}>
          <Button onClick={() => setBatchModalOpen(false)} disabled={batchSaving} color="inherit">
            ยกเลิก
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={batchSaving ? <CircularProgress size={18} color="inherit" /> : <AutoFixHighIcon />}
            onClick={handleExecuteBatchUpdate}
            disabled={batchSaving || getComputedBatchPreview().filter(p => p.hasChanged && !excludedBatchIds.includes(p.id)).length === 0}
            sx={{ fontWeight: 700, px: 3, borderRadius: 2 }}
          >
            {batchSaving ? 'กำลังบันทึก...' : `ยืนยันและบันทึกการเปลี่ยนแปลง (${getComputedBatchPreview().filter(p => p.hasChanged && !excludedBatchIds.includes(p.id)).length} แห่ง)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Co-Teacher Modal */}
      <Dialog 
        open={createCoTeacherOpen} 
        onClose={() => !createCoTeacherSaving && setCreateCoTeacherOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'secondary.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAddIcon /> เพิ่มข้อมูลครูพี่เลี้ยงใหม่
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Autocomplete
                  freeSolo
                  options={PREFIX_OPTIONS}
                  value={createCoTeacherFormData.prefix || ''}
                  onChange={(e, val) => setCreateCoTeacherFormData({ ...createCoTeacherFormData, prefix: val || '' })}
                  onInputChange={(e, val) => setCreateCoTeacherFormData({ ...createCoTeacherFormData, prefix: val })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="คำนำหน้าชื่อ"
                      placeholder="เลือกหรือพิมพ์เอง..."
                      helperText="เลือกหรือพิมพ์เพิ่มเติมได้"
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="ชื่อ"
                  value={createCoTeacherFormData.first_name}
                  onChange={(e) => setCreateCoTeacherFormData({ ...createCoTeacherFormData, first_name: e.target.value })}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="นามสกุล"
                  value={createCoTeacherFormData.last_name}
                  onChange={(e) => setCreateCoTeacherFormData({ ...createCoTeacherFormData, last_name: e.target.value })}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  options={schools}
                  getOptionLabel={(option: any) => `${option.name} (${option.affiliation || 'ไม่ระบุสังกัด'})`}
                  value={schools.find((s: any) => s.id === createCoTeacherFormData.school_id) || null}
                  onChange={(e, val: any) => setCreateCoTeacherFormData({ ...createCoTeacherFormData, school_id: val?.id || '' })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="สถานศึกษาที่สังกัด"
                      placeholder="เลือกหรือค้นหาโรงเรียน..."
                      required
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  freeSolo
                  options={MAJORS}
                  value={createCoTeacherFormData.position || ''}
                  onChange={(e, val) => setCreateCoTeacherFormData({ ...createCoTeacherFormData, position: val || '' })}
                  onInputChange={(e, val) => setCreateCoTeacherFormData({ ...createCoTeacherFormData, position: val })}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      size="small" 
                      label="กลุ่มสาระการเรียนรู้ / สาขาวิชาที่สอน" 
                      placeholder="เช่น ภาษาไทย, วิทยาศาสตร์ทั่วไป, คณิตศาสตร์..." 
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="ชื่อผู้ใช้งาน (Username)"
                  value={createCoTeacherFormData.username}
                  onChange={(e) => setCreateCoTeacherFormData({ ...createCoTeacherFormData, username: e.target.value })}
                  placeholder="เช่น somchai_k"
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="รหัสผ่าน (Password)"
                  type="password"
                  value={createCoTeacherFormData.password}
                  onChange={(e) => setCreateCoTeacherFormData({ ...createCoTeacherFormData, password: e.target.value })}
                  placeholder="เว้นว่างเพื่อใช้ 'tpms1234'"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="เบอร์โทรศัพท์"
                  value={createCoTeacherFormData.phone_number}
                  onChange={(e) => setCreateCoTeacherFormData({ ...createCoTeacherFormData, phone_number: e.target.value })}
                  placeholder="08xxxxxxxx"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="อีเมล"
                  value={createCoTeacherFormData.email}
                  onChange={(e) => setCreateCoTeacherFormData({ ...createCoTeacherFormData, email: e.target.value })}
                  placeholder="example@email.com"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCreateCoTeacherOpen(false)} color="inherit" disabled={createCoTeacherSaving}>
            ยกเลิก
          </Button>
          <Button 
            onClick={handleSaveNewCoTeacher} 
            variant="contained" 
            color="secondary" 
            disabled={createCoTeacherSaving}
            startIcon={<PersonAddIcon />}
          >
            {createCoTeacherSaving ? 'กำลังบันทึก...' : 'บันทึกครูพี่เลี้ยง'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Co-Teacher CSV Import Modal */}
      <Dialog open={coTeacherImportOpen} onClose={() => setCoTeacherImportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ImportIcon color="primary" /> นำเข้าข้อมูลครูพี่เลี้ยงจากไฟล์ CSV
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            อัปโหลดไฟล์ <b>.CSV</b> ที่มีหัวตาราง: <code>Username, Password, คำนำหน้า, ชื่อ, นามสกุล, Email, เบอร์โทร, ชื่อโรงเรียน, กลุ่มสาระ</code>
          </Alert>
          {coTeacherImportMessage.text && (
            <Alert severity={coTeacherImportMessage.type as any} sx={{ mb: 2 }}>
              {coTeacherImportMessage.text}
            </Alert>
          )}
          <Box sx={{ textAlign: 'center', p: 3, border: '2px dashed #ccc', borderRadius: 2 }}>
            <input
              accept=".csv"
              style={{ display: 'none' }}
              id="coteacher-csv-upload"
              type="file"
              onChange={handleCoTeacherFileUpload}
            />
            <label htmlFor="coteacher-csv-upload">
              <Button variant="contained" component="span" startIcon={<ImportIcon />}>
                เลือกไฟล์ CSV ครูพี่เลี้ยง
              </Button>
            </label>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
          <Button startIcon={<DownloadIcon />} onClick={handleDownloadCoTeacherTemplate}>
            ดาวน์โหลดแม่แบบ CSV (Template)
          </Button>
          <Button onClick={() => setCoTeacherImportOpen(false)}>ปิด</Button>
        </DialogActions>
      </Dialog>

      {/* Student Relocation Modal */}
      <Dialog 
        open={relocateOpen} 
        onClose={() => !relocateSaving && setRelocateOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <TransferIcon /> โยกย้ายสถานศึกษา / เปลี่ยนครูพี่เลี้ยง
        </DialogTitle>
        <DialogContent dividers>
          {selectedStudentForRelocate && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
                  {selectedStudentForRelocate.prefix}{selectedStudentForRelocate.first_name} {selectedStudentForRelocate.last_name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  รหัสนักศึกษา: <b>{selectedStudentForRelocate.student_code}</b> | สาขาวิชา: <b>{selectedStudentForRelocate.major}</b>
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                  สถานศึกษาเดิม: <b>{selectedSchoolForDetails?.name}</b>
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  ครูพี่เลี้ยงเดิม: <b>{selectedStudentForRelocate.co_teacher_first_name ? `${selectedStudentForRelocate.co_teacher_prefix || ''}${selectedStudentForRelocate.co_teacher_first_name} ${selectedStudentForRelocate.co_teacher_last_name}` : 'ยังไม่ระบุ'}</b>
                </Typography>
              </Paper>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Autocomplete
                    options={schools}
                    getOptionLabel={(option: any) => `${option.name} (${option.affiliation || 'ไม่ระบุสังกัด'})`}
                    value={schools.find((s: any) => s.id === relocateSchoolId) || null}
                    onChange={(e, val: any) => handleRelocateSchoolChange(val?.id || '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="ย้ายไปสถานศึกษา"
                        required
                        helperText="เลือกสถานศึกษาเป้าหมายที่ต้องการย้ายนักศึกษาไปฝึกสอน"
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="ครูพี่เลี้ยงคนใหม่ (สถานศึกษาเป้าหมาย)"
                    value={relocateCoTeacherId}
                    onChange={(e) => setRelocateCoTeacherId(e.target.value)}
                    disabled={relocateLoadingCoTeachers || !relocateSchoolId}
                    helperText={relocateLoadingCoTeachers ? "กำลังโหลดรายชื่อครูพี่เลี้ยง..." : "เลือกครูพี่เลี้ยงที่ดูแล หรือเลือก 'ยังไม่ระบุครูพี่เลี้ยง'"}
                  >
                    <MenuItem value="">
                      <em>-- ยังไม่ระบุครูพี่เลี้ยง --</em>
                    </MenuItem>
                    {relocateSchoolCoTeachers.map((ct: any) => (
                      <MenuItem key={ct.id} value={ct.id}>
                        {ct.prefix || ''}{ct.first_name} {ct.last_name} {ct.position ? `(${ct.position})` : ''}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="ภาคเรียน"
                    value={relocateSemester}
                    onChange={(e) => setRelocateSemester(e.target.value)}
                  >
                    <MenuItem value="1">ภาคเรียนที่ 1</MenuItem>
                    <MenuItem value="2">ภาคเรียนที่ 2</MenuItem>
                    <MenuItem value="3">ภาคเรียนฤดูร้อน (3)</MenuItem>
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="ปีการศึกษา"
                    value={relocateYear}
                    onChange={(e) => setRelocateYear(e.target.value)}
                  >
                    {availableYears.map((yr) => (
                      <MenuItem key={yr} value={yr}>ปีการศึกษา {yr}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRelocateOpen(false)} color="inherit" disabled={relocateSaving}>
            ยกเลิก
          </Button>
          <Button 
            onClick={handleSaveRelocateStudent} 
            variant="contained" 
            color="primary" 
            disabled={relocateSaving || !relocateSchoolId}
            startIcon={<TransferIcon />}
          >
            {relocateSaving ? 'กำลังบันทึก...' : 'ยืนยันการโยกย้าย'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SchoolManager;
